import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeftIcon, LayersIcon, LinkIcon, NetworkIcon } from '@app/components/icons';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useApiConfiguration } from '@app/hooks/useApiConfiguration';
import { collectCursorPages } from '@app/utils/collectCursorPages';
import type {
  Site,
  SiteIntelligenceBfdFindingDetailResponse,
  SiteIntelligenceBfdObservation,
  SiteIntelligenceOspf,
  SiteIntelligenceOspfFindingDetailResponse,
  SiteIntelligenceOspfObservation,
  SiteIntelligenceIsis,
  SiteIntelligenceIsisFindingDetailResponse,
  SiteIntelligenceIsisObservation,
  SiteIntelligenceDevice,
  SiteIntelligenceExecutive,
  SiteIntelligenceFindingPreview,
  SiteIntelligenceNamingFindingDetailResponse,
  SiteIntelligenceNamingListResponse,
  SiteIntelligenceNamingReviewQuestionListResponse,
  SiteIntelligenceReviewQuestionListResponse,
  SiteIntelligenceBgpFindingDetailResponse,
  SiteIntelligenceRoutingFindingListResponse,
  SiteIntelligenceRoutingIgp,
  SiteIntelligenceRoutingIgpResponse,
  SiteIntelligenceRoutingInstancePreview,
  SiteIntelligenceBgpObservation,
  SiteIntelligenceBgpOperatorEvidence,
  SiteIntelligenceRoutingReviewQuestionListResponse,
  SiteIntelligenceSummary,
  SiteIntelligenceTopologyFindingDetailResponse,
  SiteIntelligenceTopologyFindingListResponse,
  SiteIntelligenceTopologyReviewQuestionListResponse,
  SiteIntelligenceVlanFinding,
  SiteIntelligenceVlanFindingDetailResponse,
  SiteIntelligenceVlanDetailResponse,
  SiteIntelligenceVlanListResponse
} from '@app/types/sdk';
import { ResponseError, SitesApi } from '@app/types/sdk';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';
import TopologyV2Page from './topology/TopologyV2Page';
import { buildTopologyViewHref } from './topology/topologyCustomViews';
import { BoundarySnapshotVisual, NamingSnapshotVisual, RoutingSnapshotVisual, TopologySnapshotVisual, VlanSnapshotVisual } from './SiteIntelligenceVisuals';
import './siteIntelligence.css';

type Collection = 'naming' | 'topology' | 'vlans' | 'bgp' | 'bfd' | 'ospf' | 'isis' | 'naming-review-questions' | 'topology-review-questions' | 'bgp-review-questions' | 'bfd-review-questions' | 'ospf-review-questions' | 'isis-review-questions';
type IntelligenceTab = 'overview' | 'topology' | 'vlans' | 'naming' | 'routing';
type RoutingProtocol = 'bgp' | 'bfd' | 'ospf' | 'isis';
type CollectionSection = 'findings' | 'questions';
type GraphViewer = 'physical' | 'vlan' | null;
type ExecutiveView = 'claims' | 'reviews' | null;
type SiteOption = { id: string; name: string };

const intelligenceTabs: Array<{ id: IntelligenceTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'topology', label: 'Topology' },
  { id: 'vlans', label: 'VLANs' },
  { id: 'naming', label: 'Naming' },
  { id: 'routing', label: 'Routing' }
];
const routingProtocols: Array<{ id: RoutingProtocol; label: string }> = [
  { id: 'bgp', label: 'BGP' }, { id: 'bfd', label: 'BFD' }, { id: 'ospf', label: 'OSPF' }, { id: 'isis', label: 'IS-IS' }
];
const isRoutingProtocol = (value: string | null): value is RoutingProtocol => value === 'bgp' || value === 'bfd' || value === 'ospf' || value === 'isis';
const isIntelligenceTab = (value: string | null): value is IntelligenceTab => intelligenceTabs.some((tab) => tab.id === value);
const collectionForTab = (tab: IntelligenceTab, protocol: RoutingProtocol, section: CollectionSection): Collection | null => {
  if (tab === 'overview') return null;
  if (tab === 'vlans') return 'vlans';
  const base = tab === 'routing' ? protocol : tab;
  return section === 'questions' ? `${base}-review-questions` as Collection : base as Collection;
};
const tabForCollection = (collection: Collection): IntelligenceTab => {
  if (collection === 'vlans') return 'vlans';
  if (collection.startsWith('topology')) return 'topology';
  if (collection.startsWith('naming')) return 'naming';
  return 'routing';
};

type EvidenceSelection =
  | { kind: 'naming'; data: SiteIntelligenceNamingFindingDetailResponse }
  | { kind: 'topology'; data: SiteIntelligenceTopologyFindingDetailResponse; architectureBestFit?: string; summary?: SiteIntelligenceTopologyFindingDetailResponse; summaryUnavailable?: boolean }
  | { kind: 'bgp'; data: SiteIntelligenceBgpFindingDetailResponse }
  | { kind: 'bfd'; data: SiteIntelligenceBfdFindingDetailResponse }
  | { kind: 'ospf'; data: SiteIntelligenceOspfFindingDetailResponse }
  | { kind: 'isis'; data: SiteIntelligenceIsisFindingDetailResponse }
  | { kind: 'vlan-finding'; data: SiteIntelligenceVlanFindingDetailResponse }
  | { kind: 'vlan'; data: SiteIntelligenceVlanDetailResponse };

const findingEvidenceTitles = {
  naming: 'Naming evidence',
  topology: 'Topology evidence',
  bgp: 'BGP routing evidence',
  bfd: 'BFD evidence',
  ospf: 'OSPF evidence',
  isis: 'IS-IS evidence'
};

type CollectionError = { collection: Collection; message: string };

const architectureLabels: Record<string, string> = {
  two_tier: 'Two-tier',
  three_tier: 'Three-tier',
  collapsed_core: 'Collapsed core',
  spine_leaf: 'Spine–leaf',
  hub_and_spoke: 'Hub and spoke',
  firewall_centered_edge: 'Firewall-centered edge',
  mixed: 'Mixed',
  unclassified: 'Unclassified'
};

const architectureLabel = (value: string) => architectureLabels[value] ?? value;
type ExecutiveSection = Exclude<IntelligenceTab, 'overview'>;
const executiveSectionLabels: Record<ExecutiveSection, string> = {
  topology: 'Topology', vlans: 'VLANs', naming: 'Naming', routing: 'Routing'
};
const isExecutiveSection = (value: string): value is ExecutiveSection => value === 'topology' || value === 'vlans' || value === 'naming' || value === 'routing';
type ExecutiveReviewItem = SiteIntelligenceExecutive['reviewItems'][number];
type ExecutiveReviewTarget = { tab: ExecutiveSection; protocol?: RoutingProtocol; section: CollectionSection; label: string };

const executiveReviewTarget = (summary: SiteIntelligenceSummary, routingIgp: SiteIntelligenceRoutingIgp | null, item: ExecutiveReviewItem): ExecutiveReviewTarget | null => {
  if (!isExecutiveSection(item.section)) return null;
  if (item.section === 'routing') {
    // The public executive item has no protocol. Match its exact question to a
    // protocol preview, and omit navigation if the selected snapshot cannot identify it.
    const question = item.text.trim();
    const protocol = routingProtocols.find(({ id }) =>
      (id === 'ospf' || id === 'isis' ? routingIgp?.[id] : summary.sections.routing?.[id])?.reviewQuestionPreview?.some((preview) => preview.text.trim() === question)
    );
    return protocol ? { tab: 'routing', protocol: protocol.id, section: 'questions', label: `Open ${protocol.label} review questions` } : null;
  }
  if (item.section === 'vlans') return { tab: 'vlans', section: 'findings', label: 'Open VLAN catalog' };
  return { tab: item.section, section: 'questions', label: `Open ${executiveSectionLabels[item.section]} review questions` };
};
const reviewQuestionDescription = (count: number) => `${count} ${count === 1 ? 'question' : 'questions'} for this snapshot`;

const formatTimestamp = (value: Date) => value.toLocaleString(undefined, {
  timeZone: 'UTC',
  timeZoneName: 'short'
});

const SectionState = ({ state, failureCode }: { state: string; failureCode?: string | null }) => (
  <span className={`site-intelligence-state site-intelligence-state--${state}`}>
    {state === 'available' ? 'Observed' : state === 'partial' ? 'Partial' : state === 'notMaterialized' ? 'Not materialized' : 'Unavailable'}
    {failureCode ? ` · ${failureCode}` : ''}
  </span>
);

const Confidence = ({ value }: { value?: string | null }) => value ? (
  <span className={`site-intelligence-confidence site-intelligence-confidence--${value}`}>{value}</span>
) : null;

/** Explains why a named section has no usable result in the selected snapshot. */
const SectionUnavailableNotice = ({ section, state, failureCode }: { section: string; state: string; failureCode?: string | null }) => (
  <p className="site-intelligence-section-notice">
    {state === 'notMaterialized'
      ? `${section} was not materialized for this snapshot.`
      : `${section} is unavailable for this snapshot.`}
    {failureCode ? <span>Reason: {failureCode}</span> : null}
  </p>
);

const SiteIntelligencePage = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const orgId = useActiveOrgId();
  const apiConfiguration = useApiConfiguration();
  const sitesApi = useMemo(() => new SitesApi(apiConfiguration), [apiConfiguration]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = isIntelligenceTab(searchParams.get('tab')) ? searchParams.get('tab') as IntelligenceTab : 'overview';
  const activeProtocol = isRoutingProtocol(searchParams.get('protocol')) ? searchParams.get('protocol') as RoutingProtocol : 'bgp';
  const activeSection: CollectionSection = searchParams.get('section') === 'questions' ? 'questions' : 'findings';
  const activeCollection = collectionForTab(activeTab, activeProtocol, activeSection);
  const [summary, setSummary] = useState<SiteIntelligenceSummary | null>(null);
  const [routingIgp, setRoutingIgp] = useState<SiteIntelligenceRoutingIgpResponse | null>(null);
  const [routingIgpError, setRoutingIgpError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [siteOptions, setSiteOptions] = useState<SiteOption[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState(false);
  const [expandedCollection, setExpandedCollection] = useState<Collection | null>(null);
  const [naming, setNaming] = useState<SiteIntelligenceNamingListResponse | null>(null);
  const [namingReviewQuestions, setNamingReviewQuestions] = useState<SiteIntelligenceNamingReviewQuestionListResponse | null>(null);
  const [topology, setTopology] = useState<SiteIntelligenceTopologyFindingListResponse | null>(null);
  const [topologyReviewQuestions, setTopologyReviewQuestions] = useState<SiteIntelligenceTopologyReviewQuestionListResponse | null>(null);
  const [bgpFindings, setBgpFindings] = useState<SiteIntelligenceRoutingFindingListResponse | null>(null);
  const [bgpReviewQuestions, setBgpReviewQuestions] = useState<SiteIntelligenceRoutingReviewQuestionListResponse | null>(null);
  const [bfd, setBfd] = useState<SiteIntelligenceRoutingFindingListResponse | null>(null);
  const [bfdReviewQuestions, setBfdReviewQuestions] = useState<SiteIntelligenceRoutingReviewQuestionListResponse | null>(null);
  const [ospfFindings, setOspfFindings] = useState<SiteIntelligenceRoutingFindingListResponse | null>(null);
  const [ospfReviewQuestions, setOspfReviewQuestions] = useState<SiteIntelligenceRoutingReviewQuestionListResponse | null>(null);
  const [isisFindings, setIsisFindings] = useState<SiteIntelligenceRoutingFindingListResponse | null>(null);
  const [isisReviewQuestions, setIsisReviewQuestions] = useState<SiteIntelligenceRoutingReviewQuestionListResponse | null>(null);
  const [vlans, setVlans] = useState<SiteIntelligenceVlanListResponse | null>(null);
  const [collectionLoading, setCollectionLoading] = useState<Collection | null>(null);
  const [collectionError, setCollectionError] = useState<CollectionError | null>(null);
  const [evidence, setEvidence] = useState<EvidenceSelection | null>(null);
  const [evidenceTitle, setEvidenceTitle] = useState('Evidence');
  const [evidenceLoading, setEvidenceLoading] = useState<string | null>(null);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const [graphViewer, setGraphViewer] = useState<GraphViewer>(null);
  const [executiveView, setExecutiveView] = useState<ExecutiveView>(null);
  const closeGraphViewer = useCallback(() => setGraphViewer(null), []);
  // Every response is scoped to the route's organization and site. This
  // invalidates in-flight collections and evidence when either changes.
  const scopeVersionRef = useRef(0);
  // Closing the evidence dialog or opening another finding invalidates its prior
  // request, so a late response cannot reopen evidence the user dismissed.
  const evidenceRequestVersionRef = useRef(0);
  const pageRef = useRef<HTMLDivElement | null>(null);

  const loadSummary = useCallback(async (signal: AbortSignal, scopeVersion: number) => {
    const isCurrentScope = () => !signal.aborted && scopeVersionRef.current === scopeVersion;
    if (!orgId || !siteId) {
      if (!isCurrentScope()) return;
      setLoading(false);
      setError('Choose a site to view Site Intelligence.');
      return;
    }
    if (!isCurrentScope()) return;
    setLoading(true);
    setError(null);
    try {
      const response = await sitesApi.getSiteIntelligenceSummary(
        { orgId, siteId },
        { signal }
      );
      if (!isCurrentScope()) return;
      setSummary(response);
    } catch (caught) {
      if (!isCurrentScope()) return;
      if (caught instanceof ResponseError && caught.response.status === 404) {
        setError('No completed Site Intelligence snapshot exists for this site yet.');
      } else if (!(caught instanceof Error && caught.name === 'AbortError')) {
        setError('Unable to load Site Intelligence.');
      }
      setSummary(null);
    } finally {
      if (isCurrentScope()) setLoading(false);
    }
  }, [orgId, siteId, sitesApi]);

  useLayoutEffect(() => {
    const scopeVersion = ++scopeVersionRef.current;
    evidenceRequestVersionRef.current += 1;
    setSummary(null);
    setRoutingIgp(null);
    setRoutingIgpError(false);
    setLoading(true);
    setError(null);
    setExpandedCollection(null);
    setNaming(null);
    setNamingReviewQuestions(null);
    setTopology(null);
    setTopologyReviewQuestions(null);
    setBgpFindings(null);
    setBgpReviewQuestions(null);
    setBfd(null);
    setBfdReviewQuestions(null);
    setOspfFindings(null);
    setOspfReviewQuestions(null);
    setIsisFindings(null);
    setIsisReviewQuestions(null);
    setVlans(null);
    setCollectionLoading(null);
    setCollectionError(null);
    setEvidence(null);
    setEvidenceTitle('Evidence');
    setEvidenceLoading(null);
    setEvidenceError(null);
    setGraphViewer(null);
    setExecutiveView(null);
    return () => {
      if (scopeVersionRef.current === scopeVersion) scopeVersionRef.current += 1;
    };
  }, [orgId, siteId]);

  useEffect(() => {
    const controller = new AbortController();
    void loadSummary(controller.signal, scopeVersionRef.current);
    return () => controller.abort();
  }, [loadSummary]);

  useEffect(() => {
    if (!summary || !orgId || !siteId) return;
    const controller = new AbortController();
    const scopeVersion = scopeVersionRef.current;
    setRoutingIgp(null);
    setRoutingIgpError(false);
    void sitesApi.getSiteIntelligenceRoutingIgp(
      { orgId, siteId, snapshotId: summary.snapshot.id },
      { signal: controller.signal }
    ).then((response) => {
      if (!controller.signal.aborted && scopeVersionRef.current === scopeVersion && response.snapshot?.id === summary.snapshot.id) {
        if (response.routingIgp?.ospf && response.routingIgp?.isis) setRoutingIgp(response);
        else setRoutingIgpError(true);
      }
    }).catch(() => {
      if (!controller.signal.aborted && scopeVersionRef.current === scopeVersion) setRoutingIgpError(true);
    });
    return () => controller.abort();
  }, [summary, orgId, siteId, sitesApi]);

  useEffect(() => {
    const controller = new AbortController();
    setSiteOptions([]);
    setSitesError(false);
    if (!orgId) return () => controller.abort();
    setSitesLoading(true);
    void collectCursorPages<Site>((after) => sitesApi.orgsOrgIdSitesGet(
      { orgId, limit: 200, after },
      { signal: controller.signal }
    )).then(({ items }) => {
      if (controller.signal.aborted) return;
      setSiteOptions(items.flatMap((site) => site.id ? [{ id: site.id, name: site.name }] : [])
        .sort((left, right) => left.name.localeCompare(right.name) || left.id.localeCompare(right.id)));
    }).catch(() => {
      if (!controller.signal.aborted) setSitesError(true);
    }).finally(() => {
      if (!controller.signal.aborted) setSitesLoading(false);
    });
    return () => controller.abort();
  }, [orgId, sitesApi]);

  const loadCollection = async (collection: Collection, after?: string | null) => {
    const scopeVersion = scopeVersionRef.current;
    const isCurrentScope = () => scopeVersionRef.current === scopeVersion;
    if (!isCurrentScope()) return;
    setExpandedCollection(collection);
    setCollectionLoading(collection);
    setCollectionError(null);
    try {
      if (collection === 'naming') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceNamingFamilies({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setNaming((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      } else if (collection === 'naming-review-questions') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceNamingReviewQuestions({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setNamingReviewQuestions((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      } else if (collection === 'topology') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceTopologyFindings({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setTopology((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      } else if (collection === 'topology-review-questions') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceTopologyReviewQuestions({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setTopologyReviewQuestions((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      } else if (collection === 'bgp') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceBgpFindings({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setBgpFindings((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      } else if (collection === 'bgp-review-questions') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceBgpReviewQuestions({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setBgpReviewQuestions((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      } else if (collection === 'bfd') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceBfdFindings({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setBfd((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      } else if (collection === 'bfd-review-questions') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceBfdReviewQuestions({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setBfdReviewQuestions((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      } else if (collection === 'ospf' || collection === 'isis') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const request = { orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined };
        const response = collection === 'ospf' ? await sitesApi.listSiteIntelligenceOspfFindings(request) : await sitesApi.listSiteIntelligenceIsisFindings(request);
        if (!isCurrentScope()) return;
        const update = (current: SiteIntelligenceRoutingFindingListResponse | null) => after && current ? { ...response, items: [...current.items, ...response.items] } : response;
        if (collection === 'ospf') setOspfFindings(update); else setIsisFindings(update);
      } else if (collection === 'ospf-review-questions' || collection === 'isis-review-questions') {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const request = { orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined };
        const response = collection === 'ospf-review-questions' ? await sitesApi.listSiteIntelligenceOspfReviewQuestions(request) : await sitesApi.listSiteIntelligenceIsisReviewQuestions(request);
        if (!isCurrentScope()) return;
        const update = (current: SiteIntelligenceRoutingReviewQuestionListResponse | null) => after && current ? { ...response, items: [...current.items, ...response.items] } : response;
        if (collection === 'ospf-review-questions') setOspfReviewQuestions(update); else setIsisReviewQuestions(update);
      } else {
        if (!orgId || !siteId) throw new Error('An organization and site are required.');
        const response = await sitesApi.listSiteIntelligenceVlans({ orgId, siteId, snapshotId: summary?.snapshot.id, limit: 50, after: after ?? undefined });
        if (!isCurrentScope()) return;
        setVlans((current) => after && current ? { ...response, items: [...current.items, ...response.items] } : response);
      }
    } catch (caught) {
      if (isCurrentScope() && !(caught instanceof Error && caught.name === 'AbortError')) {
        setCollectionError({ collection, message: 'Unable to load this collection. The other Site Intelligence sections are still available.' });
      }
    } finally {
      if (isCurrentScope()) {
        // A request for another collection may have started while this one was
        // in flight. Do not hide that newer collection's loading state.
        setCollectionLoading((current) => current === collection ? null : current);
      }
    }
  };

  useEffect(() => {
    if (summary?.snapshot.id && activeCollection) void loadCollection(activeCollection);
  }, [summary?.snapshot.id, activeCollection]);

  const selectTab = (tab: IntelligenceTab, protocol: RoutingProtocol = activeProtocol, section: CollectionSection = 'findings') => {
    evidenceRequestVersionRef.current += 1;
    setEvidence(null);
    setEvidenceLoading(null);
    setEvidenceError(null);
    setExpandedCollection(collectionForTab(tab, protocol, section));
    const scrollContainer = pageRef.current?.closest('.org-module')?.querySelector<HTMLElement>('.org-module__scroll');
    if (scrollContainer && typeof scrollContainer.scrollTo === 'function') scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (tab === 'overview') next.delete('tab'); else next.set('tab', tab);
      if (tab === 'routing') next.set('protocol', protocol); else next.delete('protocol');
      if (tab !== 'overview' && tab !== 'vlans' && section === 'questions') next.set('section', 'questions'); else next.delete('section');
      return next;
    });
  };

  const openCollection = (collection: Collection) => {
    const tab = tabForCollection(collection);
    const protocol = isRoutingProtocol(collection.split('-')[0]) ? collection.split('-')[0] as RoutingProtocol : activeProtocol;
    const section = collection.endsWith('-review-questions') ? 'questions' : 'findings';
    selectTab(tab, protocol, section);
  };

  const minimizeCollection = () => {
    selectTab('overview');
  };

  const openFinding = async (kind: 'naming' | 'topology' | 'bgp' | 'bfd' | 'ospf' | 'isis', finding: { findingId: string }) => {
    const scopeVersion = scopeVersionRef.current;
    const evidenceRequestVersion = ++evidenceRequestVersionRef.current;
    const isCurrentRequest = () => scopeVersionRef.current === scopeVersion && evidenceRequestVersionRef.current === evidenceRequestVersion;
    setEvidence(null);
    setEvidenceTitle(findingEvidenceTitles[kind]);
    setEvidenceError(null);
    setEvidenceLoading('Loading observed evidence…');
    try {
      if (!orgId || !siteId) throw new Error('An organization and site are required.');
      if (kind === 'naming') {
        const response = await sitesApi.getSiteIntelligenceNamingFinding({ orgId, siteId, findingId: finding.findingId, snapshotId: summary?.snapshot.id });
        if (!isCurrentRequest()) return;
        setEvidence({ kind, data: response });
      } else if (kind === 'topology') {
        const response = await sitesApi.getSiteIntelligenceTopologyFinding({ orgId, siteId, findingId: finding.findingId, snapshotId: summary?.snapshot.id });
        if (!isCurrentRequest()) return;
        setEvidence({ kind, data: response });
      } else if (kind === 'bgp') {
        const response = await sitesApi.getSiteIntelligenceBgpFinding({ orgId, siteId, findingId: finding.findingId, snapshotId: summary?.snapshot.id });
        if (!isCurrentRequest()) return;
        setEvidence({ kind, data: response });
      } else if (kind === 'ospf') {
        const response = await sitesApi.getSiteIntelligenceOspfFinding({ orgId, siteId, findingId: finding.findingId, snapshotId: summary?.snapshot.id });
        if (!isCurrentRequest()) return;
        setEvidence({ kind, data: response });
      } else if (kind === 'isis') {
        const response = await sitesApi.getSiteIntelligenceIsisFinding({ orgId, siteId, findingId: finding.findingId, snapshotId: summary?.snapshot.id });
        if (!isCurrentRequest()) return;
        setEvidence({ kind, data: response });
      } else {
        const response = await sitesApi.getSiteIntelligenceBfdFinding({ orgId, siteId, findingId: finding.findingId, snapshotId: summary?.snapshot.id });
        if (!isCurrentRequest()) return;
        setEvidence({ kind, data: response });
      }
    } catch {
      if (isCurrentRequest()) setEvidenceError('Unable to load the evidence for this finding.');
    } finally {
      if (isCurrentRequest()) setEvidenceLoading(null);
    }
  };

  // The architecture category and prose summary are separate public findings
  // with potentially different evidence. Show both in one evidence dialog so
  // each statement remains grounded without extra navigation.
  const openArchitectureEvidence = async (architecture: NonNullable<SiteIntelligenceSummary['sections']['topology']['architecture']>, topologySummary?: SiteIntelligenceFindingPreview) => {
    const scopeVersion = scopeVersionRef.current;
    const evidenceRequestVersion = ++evidenceRequestVersionRef.current;
    const isCurrentRequest = () => scopeVersionRef.current === scopeVersion && evidenceRequestVersionRef.current === evidenceRequestVersion;
    setEvidence(null);
    setEvidenceTitle('Topology evidence');
    setEvidenceError(null);
    setEvidenceLoading('Loading observed evidence…');
    try {
      if (!orgId || !siteId) throw new Error('An organization and site are required.');
      const architectureRequest = sitesApi.getSiteIntelligenceTopologyFinding({ orgId, siteId, findingId: architecture.findingId, snapshotId: summary?.snapshot.id });
      const summaryRequest = topologySummary
        ? sitesApi.getSiteIntelligenceTopologyFinding({ orgId, siteId, findingId: topologySummary.findingId, snapshotId: summary?.snapshot.id }).catch(() => undefined)
        : Promise.resolve(undefined);
      const [architectureEvidence, summaryEvidence] = await Promise.all([architectureRequest, summaryRequest]);
      if (!isCurrentRequest()) return;
      setEvidence({ kind: 'topology', data: architectureEvidence, architectureBestFit: architecture.bestFit, summary: summaryEvidence, summaryUnavailable: Boolean(topologySummary && !summaryEvidence) });
    } catch {
      if (isCurrentRequest()) setEvidenceError('Unable to load the evidence for this finding.');
    } finally {
      if (isCurrentRequest()) setEvidenceLoading(null);
    }
  };

  const openVlan = async (vlanId: number) => {
    const scopeVersion = scopeVersionRef.current;
    const evidenceRequestVersion = ++evidenceRequestVersionRef.current;
    const isCurrentRequest = () => scopeVersionRef.current === scopeVersion && evidenceRequestVersionRef.current === evidenceRequestVersion;
    setEvidence(null);
    setEvidenceTitle(`VLAN ${vlanId}`);
    setEvidenceError(null);
    setEvidenceLoading(`Loading observed VLAN ${vlanId}…`);
    try {
      if (!orgId || !siteId) throw new Error('An organization and site are required.');
      const response = await sitesApi.getSiteIntelligenceVlan({ orgId, siteId, vlanId, snapshotId: summary?.snapshot.id });
      if (!isCurrentRequest()) return;
      setEvidence({ kind: 'vlan', data: response });
    } catch {
      if (isCurrentRequest()) setEvidenceError(`Unable to load observed VLAN ${vlanId}.`);
    } finally {
      if (isCurrentRequest()) setEvidenceLoading(null);
    }
  };

  const openVlanFinding = async (finding: { findingId: string }, title: string) => {
    const scopeVersion = scopeVersionRef.current;
    const evidenceRequestVersion = ++evidenceRequestVersionRef.current;
    const isCurrentRequest = () => scopeVersionRef.current === scopeVersion && evidenceRequestVersionRef.current === evidenceRequestVersion;
    setEvidence(null);
    setEvidenceTitle(title);
    setEvidenceError(null);
    setEvidenceLoading('Loading boundary evidence…');
    try {
      if (!orgId || !siteId) throw new Error('An organization and site are required.');
      const response = await sitesApi.getSiteIntelligenceVlanFinding({ orgId, siteId, findingId: finding.findingId, snapshotId: summary?.snapshot.id });
      if (!isCurrentRequest()) return;
      setEvidence({ kind: 'vlan-finding', data: response });
    } catch {
      if (isCurrentRequest()) setEvidenceError('Unable to load the boundary evidence.');
    } finally {
      if (isCurrentRequest()) setEvidenceLoading(null);
    }
  };

  const closeEvidence = useCallback(() => {
    evidenceRequestVersionRef.current += 1;
    setEvidence(null);
    setEvidenceTitle('Evidence');
    setEvidenceLoading(null);
    setEvidenceError(null);
  }, []);

  const selectSite = (nextSiteId: string) => {
    if (!nextSiteId || nextSiteId === siteId) return;
    const search = searchParams.toString();
    navigate({
      pathname: `/app/sites/${encodeURIComponent(nextSiteId)}/intelligence`,
      search: search ? `?${search}` : ''
    });
  };

  if (!orgId) {
    return <OrgModuleLayout title="Site Intelligence" description="Observed site facts and evidence-backed interpretations."><OrgModulePlaceholder intent="Select an organization to view a site." /></OrgModuleLayout>;
  }

  const topologyPreview = summary?.sections.topology;
  const executive = summary?.executive?.state === 'available' && summary.executive.headline && summary.executive.summary
    ? summary.executive : null;
  const executiveHasDesign = executive?.claims.some((claim) => claim.category === 'design_hypothesis') ?? false;
  const observed = topologyPreview?.observed;
  const topologyAvailable = topologyPreview?.state === 'available';
  const namingAvailable = summary?.sections.naming.state === 'available';
  const bgpPreview = summary?.sections.routing?.bgp;
  const bgpAvailable = bgpPreview?.state === 'available';
  const bfdPreview = summary?.sections.routing?.bfd;
  const bfdAvailable = bfdPreview?.state === 'available';
  const ospfPreview = routingIgp?.routingIgp?.ospf;
  const isisPreview = routingIgp?.routingIgp?.isis;
  const vlansAvailable = summary?.sections.vlans.state === 'available';
  const topologyHasUnpreviewedQuestions = (topologyPreview?.reviewQuestionCount ?? 0) > (topologyPreview?.reviewQuestionPreview?.length ?? 0);
  const namingHasUnpreviewedQuestions = (summary?.sections.naming.reviewQuestionCount ?? 0) > (summary?.sections.naming.reviewQuestionPreview?.length ?? 0);
  return (
    <OrgModuleLayout
      title="Site Intelligence"
      description="Observed site facts and cautious interpretations, with evidence available on demand."
      actions={<button type="button" className="ui-button ui-button--ghost" onClick={() => navigate('/app/connectivity')}><ChevronLeftIcon size={16} />Back to sites</button>}
      scrollMode="all"
    >
      <div className="site-intelligence-page" ref={pageRef}>
          <header className="site-intelligence-page__header">
            <div className="site-intelligence-page__header-main">
              <div className="site-intelligence-page__title">
                <p>Site Intelligence snapshot</p>
                <h2>Observed site summary</h2>
              </div>
              <div className="site-intelligence-page__controls">
                <label className="site-intelligence-page__site-picker">
                  <span>Site</span>
                  <select value={siteId ?? ''} onChange={(event) => selectSite(event.target.value)}>
                    {!siteId ? <option value="">Choose a site</option> : null}
                    {siteId && !siteOptions.some((site) => site.id === siteId) ? <option value={siteId}>{siteId}</option> : null}
                    {siteOptions.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
                  </select>
                </label>
                <div className="site-intelligence-page__snapshot">
                  {summary ? <SectionState state={summary.snapshot.status} /> : null}
                  {summary ? <span>{summary.coverage.deviceCount} inventory {summary.coverage.deviceCount === 1 ? 'device' : 'devices'} · Generated {formatTimestamp(summary.snapshot.generatedAt)}</span> : null}
                </div>
              </div>
            </div>
            {sitesLoading ? <span className="site-intelligence-page__sites-status" role="status">Loading sites…</span> : null}
            {sitesError ? <span className="site-intelligence-page__sites-status" role="status">Site list unavailable</span> : null}
            <nav className="site-intelligence-tabs" aria-label="Site Intelligence sections">
              {intelligenceTabs.map((tab) => <button key={tab.id} type="button" className={activeTab === tab.id ? 'site-intelligence-tabs__tab is-active' : 'site-intelligence-tabs__tab'} aria-current={activeTab === tab.id ? 'page' : undefined} disabled={!summary} onClick={() => selectTab(tab.id)}>{tab.label}</button>)}
            </nav>
          </header>

          {loading ? <OrgModulePlaceholder intent="Loading Site Intelligence…" /> : null}
          {error ? <OrgModulePlaceholder intent={error} /> : null}
          {summary ? <>
          {activeTab === 'overview' ? <>
          {executive ? <section className="site-intelligence-executive" aria-labelledby="site-intelligence-executive-title">
            <div className="site-intelligence-executive__copy">
              <span className="site-intelligence-executive__eyebrow">Executive interpretation · selected snapshot</span>
              <h2 id="site-intelligence-executive-title">{executive.headline}</h2>
              <p className="site-intelligence-executive__summary">{executive.summary}</p>
              <div className="site-intelligence-executive__tags">
                <span>{executiveHasDesign ? 'Apparent design' : 'Observed reading'}</span>
                {executive.confidence ? <span>{executive.confidence[0].toUpperCase() + executive.confidence.slice(1)} confidence</span> : null}
                <span>{executive.reviewItemCount} review {executive.reviewItemCount === 1 ? 'item' : 'items'}</span>
              </div>
              <div className="site-intelligence-executive__actions">
                <button type="button" onClick={() => setExecutiveView('claims')}>Explore {executiveHasDesign ? 'design' : 'reading'} evidence</button>
                {executive.reviewItemCount > 0 ? <button type="button" onClick={() => setExecutiveView('reviews')}>Review attention items</button> : null}
              </div>
            </div>
            <aside className="site-intelligence-executive__signal" aria-label="Executive interpretation guide">
              <div className="site-intelligence-executive__signal-ring"><span>Site<br />{executiveHasDesign ? 'design' : 'reading'}</span></div>
              <div><b>What we see</b><span>Selected section facts</span></div>
              <div><b>What it suggests</b><span>{executiveHasDesign ? 'Candidate design patterns' : 'Cross-section reading'}</span></div>
              <div><b>What to validate</b><span>{executive.reviewItemCount ? 'Review questions' : 'No review items'}</span></div>
            </aside>
          </section> : null}
          <section className="site-intelligence-overview" aria-label="Observed topology facts">
            <div className="site-intelligence-overview__heading">
              <div className="site-intelligence-overview__heading-main">
                <NetworkIcon size={20} />
                <div>
                  <p>Physical topology</p>
                  <div className="site-intelligence-overview__architecture">
                    <h3>{topologyPreview?.architecture ? `${architectureLabel(topologyPreview.architecture.bestFit)} architecture` : 'No architecture interpretation'}</h3>
                    <Confidence value={topologyPreview?.architecture?.confidence} />
                  </div>
                </div>
              </div>
              <SectionState state={topologyPreview?.state ?? 'notMaterialized'} failureCode={topologyPreview?.failureCode} />
            </div>
            {topologyPreview?.summary ? <p className="site-intelligence-overview__summary">{topologyPreview.summary.text}</p> : <p className="site-intelligence-overview__summary">No topology interpretation is available for this snapshot.</p>}
            {observed ? <dl className="site-intelligence-stat-grid">
              <div><dt>Matched devices</dt><dd>{observed.matchedDeviceCount} / {observed.inventoryDeviceCount}</dd></div>
              <div><dt>Physical adjacencies</dt><dd>{observed.physicalAdjacencyCount}</dd></div>
              <div><dt>Physical connections</dt><dd>{observed.physicalConnectionCount}</dd></div>
              <div><dt>HA pairs</dt><dd>{observed.haPairCount}</dd></div>
              <div><dt>Components</dt><dd>{observed.componentCount}</dd></div>
              <div><dt>Isolated devices</dt><dd>{observed.isolatedDeviceCount}</dd></div>
            </dl> : null}
            {topologyAvailable ? <><div className="site-intelligence-overview__actions">
              {topologyPreview?.architecture ? <button type="button" className="ui-button ui-button--ghost" onClick={() => void openArchitectureEvidence(topologyPreview.architecture!, topologyPreview.summary)}>View architecture evidence</button> : null}
              <button type="button" className="ui-button ui-button--ghost" onClick={() => openCollection('topology')}>View topology findings{topologyPreview?.findingCount ? ` (${topologyPreview.findingCount})` : ''}</button>
              {topologyHasUnpreviewedQuestions ? <button type="button" className="ui-button ui-button--ghost" onClick={() => openCollection('topology-review-questions')}>View all topology review questions ({topologyPreview!.reviewQuestionCount})</button> : null}
            </div>
            {topologyPreview?.reviewQuestionPreview?.length ? <FindingPreviewList title="Topology review questions" findings={topologyPreview.reviewQuestionPreview} onOpen={(finding) => void openFinding('topology', finding)} /> : null}</> : <SectionUnavailableNotice section="Physical topology" state={topologyPreview?.state ?? 'notMaterialized'} failureCode={topologyPreview?.failureCode} />}
          </section>

          <div className="site-intelligence-section-grid">
            <section className="site-intelligence-section-card">
              <header><div><LayersIcon size={20} /><div><p>Hostname naming</p><h3>{summary.sections.naming.familyCount ?? 0} observed {summary.sections.naming.familyCount === 1 ? 'family' : 'families'}</h3></div></div><SectionState state={summary.sections.naming.state} failureCode={summary.sections.naming.failureCode} /></header>
              {namingAvailable ? <><p>{summary.sections.naming.summary?.text ?? 'No naming interpretation is available for this snapshot.'}</p>
              <dl className="site-intelligence-inline-stats"><div><dt>Variants</dt><dd>{summary.sections.naming.variantCount ?? 0}</dd></div><div><dt>Review questions</dt><dd>{summary.sections.naming.reviewQuestionCount ?? 0}</dd></div></dl>
              <div className="site-intelligence-overview__actions">
                {summary.sections.naming.summary ? <button type="button" className="ui-button ui-button--ghost" onClick={() => void openFinding('naming', summary.sections.naming.summary!)}>View naming summary evidence</button> : null}
                <button type="button" className="ui-button ui-button--ghost" onClick={() => openCollection('naming')}>View naming families</button>
                {namingHasUnpreviewedQuestions ? <button type="button" className="ui-button ui-button--ghost" onClick={() => openCollection('naming-review-questions')}>View all naming review questions ({summary.sections.naming.reviewQuestionCount})</button> : null}
              </div>
              {summary.sections.naming.reviewQuestionPreview?.length ? <FindingPreviewList title="Review questions" findings={summary.sections.naming.reviewQuestionPreview} onOpen={(finding) => void openFinding('naming', finding)} /> : null}</> : <SectionUnavailableNotice section="Hostname naming" state={summary.sections.naming.state} failureCode={summary.sections.naming.failureCode} />}
            </section>

            <section className="site-intelligence-section-card">
              <header><div><LinkIcon size={20} /><div><p>Observed VLAN catalog</p><h3>{summary.sections.vlans.vlanCount ?? 0} VLANs</h3></div></div><SectionState state={summary.sections.vlans.state} failureCode={summary.sections.vlans.failureCode} /></header>
              {vlansAvailable ? <><p>{summary.sections.vlans.summary?.text ?? 'No VLAN catalog is available for this snapshot.'}</p>
              <dl className="site-intelligence-inline-stats"><div><dt>Member devices</dt><dd>{summary.sections.vlans.memberDeviceCount ?? 0}</dd></div><div><dt>Member interfaces</dt><dd>{summary.sections.vlans.memberInterfaceCount ?? 0}</dd></div></dl>
              <button type="button" className="ui-button ui-button--ghost" onClick={() => openCollection('vlans')}>View VLAN catalog</button></> : <SectionUnavailableNotice section="Observed VLAN catalog" state={summary.sections.vlans.state} failureCode={summary.sections.vlans.failureCode} />}
            </section>

            <section className="site-intelligence-routing-group" aria-label="Routing">
              <header><h3>Routing</h3><p>Protocol-specific observations from this snapshot</p></header>
              <div className="site-intelligence-section-grid">
                <section className="site-intelligence-section-card site-intelligence-routing-card">
                  <header><div><NetworkIcon size={20} /><div><p>BGP routing adjacency</p><h3>{bgpPreview?.resolvedAdjacencyCount ?? 0} resolved in-site links</h3></div></div><SectionState state={bgpPreview?.state ?? 'notMaterialized'} failureCode={bgpPreview?.failureCode} /></header>
                  {bgpAvailable ? <><section className="site-intelligence-routing-overview">
                    <div><span>Observed overview</span>{bgpPreview.summary ? <button type="button" className="ui-button ui-button--ghost" onClick={() => void openFinding('bgp', bgpPreview.summary!)}>Evidence</button> : null}</div>
                    <p>{bgpPreview.summary?.text ?? 'No BGP overview is available for this snapshot.'}</p>
                  </section>
                  <dl className="site-intelligence-inline-stats"><div><dt>BGP instances</dt><dd>{bgpPreview.instanceCount ?? 0}</dd></div><div><dt>Review questions</dt><dd>{bgpPreview.reviewQuestionCount ?? 0}</dd></div></dl>
                  <div className="site-intelligence-routing-actions">
                    <button type="button" className="ui-button ui-button--primary" onClick={() => openCollection('bgp')}>Explore BGP findings</button>
                    {(bgpPreview.reviewQuestionCount ?? 0) > 0 ? <button type="button" className="ui-button ui-button--ghost" onClick={() => openCollection('bgp-review-questions')}>Review questions</button> : null}
                  </div>
                  {bgpPreview.instancePreview?.length ? <BgpInstancePreviewList instances={bgpPreview.instancePreview} onOpen={(finding) => void openFinding('bgp', finding)} /> : null}
                  {bgpPreview.commonalityPreview?.length ? <FindingPreviewList title="Common observed patterns" findings={bgpPreview.commonalityPreview} onOpen={(finding) => void openFinding('bgp', finding)} /> : null}
                  {bgpPreview.observedDifferencePreview?.length ? <FindingPreviewList title="Observed differences and attention" findings={bgpPreview.observedDifferencePreview} onOpen={(finding) => void openFinding('bgp', finding)} /> : null}
                  </> : <SectionUnavailableNotice section="BGP routing adjacency" state={bgpPreview?.state ?? 'notMaterialized'} failureCode={bgpPreview?.failureCode} />}
                </section>

                <section className="site-intelligence-section-card site-intelligence-routing-card">
                  <header><div><NetworkIcon size={20} /><div><p>BFD adjacency</p><h3>{bfdPreview?.resolvedAdjacencyCount == null ? 'No BFD result in this snapshot' : `${bfdPreview.resolvedAdjacencyCount} resolved in-site links`}</h3></div></div><SectionState state={bfdPreview?.state ?? 'notMaterialized'} failureCode={bfdPreview?.failureCode} /></header>
                  {bfdAvailable ? <><section className="site-intelligence-routing-overview">
                    <div><span>Observed overview</span>{bfdPreview.summary ? <button type="button" className="ui-button ui-button--ghost" onClick={() => void openFinding('bfd', bfdPreview.summary!)}>Evidence</button> : null}</div>
                    <p>{bfdPreview.summary?.text ?? 'No BFD overview is available for this snapshot.'}</p>
                  </section>
                  <p className="site-intelligence-routing-note">BFD state is source reported at this snapshot, not a live health verdict.</p>
                  <dl className="site-intelligence-inline-stats"><div><dt>BFD instances</dt><dd>{bfdPreview.instanceCount ?? 0}</dd></div><div><dt>Review questions</dt><dd>{bfdPreview.reviewQuestionCount ?? 0}</dd></div></dl>
                  <div className="site-intelligence-routing-actions">
                    <button type="button" className="ui-button ui-button--primary" onClick={() => openCollection('bfd')}>Explore BFD findings</button>
                    {(bfdPreview.reviewQuestionCount ?? 0) > 0 ? <button type="button" className="ui-button ui-button--ghost" onClick={() => openCollection('bfd-review-questions')}>BFD review questions</button> : null}
                  </div>
                  {bfdPreview.instancePreview?.length ? <BfdInstancePreviewList instances={bfdPreview.instancePreview} onOpen={(finding) => void openFinding('bfd', finding)} /> : null}
                  {bfdPreview.commonalityPreview?.length ? <FindingPreviewList title="BFD common observed patterns" findings={bfdPreview.commonalityPreview} onOpen={(finding) => void openFinding('bfd', finding)} /> : null}
                  {bfdPreview.observedDifferencePreview?.length ? <FindingPreviewList title="BFD observed differences" findings={bfdPreview.observedDifferencePreview} onOpen={(finding) => void openFinding('bfd', finding)} /> : null}
                  </> : <SectionUnavailableNotice section="BFD adjacency" state={bfdPreview?.state ?? 'notMaterialized'} failureCode={bfdPreview?.failureCode} />}
                </section>
                {routingIgp ? <>
                  <IgpCard protocol="ospf" title="OSPF" preview={ospfPreview} onCollection={openCollection} onFinding={(finding) => void openFinding('ospf', finding)} />
                  <IgpCard protocol="isis" title="IS-IS" preview={isisPreview} onCollection={openCollection} onFinding={(finding) => void openFinding('isis', finding)} />
                </> : <p className="site-intelligence-section-notice">{routingIgpError ? 'Unable to load the IGP overview for this snapshot.' : 'Loading the IGP overview…'}</p>}
              </div>
            </section>
          </div>

          </> : <>
            <header className="site-intelligence-tab-heading">
              <div><p>Snapshot detail</p><h2>{activeTab === 'topology' ? 'Physical topology' : activeTab === 'vlans' ? 'Observed VLANs' : activeTab === 'naming' ? 'Hostname naming' : 'Routing adjacency'}</h2></div>
              {activeTab === 'topology' || activeTab === 'vlans' ? <button type="button" className="ui-button ui-button--primary" onClick={() => setGraphViewer(activeTab === 'topology' ? 'physical' : 'vlan')}>{activeTab === 'topology' ? 'View physical topology' : 'View VLAN membership'}</button> : null}
            </header>
              {activeTab === 'routing' ? <nav className="site-intelligence-protocol-tabs" aria-label="Routing protocols">{routingProtocols.map((protocol) => <button type="button" key={protocol.id} className={activeProtocol === protocol.id ? 'is-active' : ''} aria-pressed={activeProtocol === protocol.id} onClick={() => selectTab('routing', protocol.id, activeSection)}>{protocol.label}</button>)}</nav> : null}
              {activeTab === 'topology' ? <TopologySnapshotVisual summary={summary} /> : null}
              {activeTab === 'vlans' ? <VlanSnapshotVisual summary={summary} list={vlans} /> : null}
              {activeTab === 'vlans' ? <VlanBoundarySection summary={summary} list={vlans} onEvidence={(finding) => void openVlanFinding(finding, 'Site boundary evidence')} /> : null}
              {activeTab === 'naming' ? <NamingSnapshotVisual summary={summary} list={naming} /> : null}
              {activeTab === 'routing' ? <RoutingSnapshotVisual summary={summary} routingIgp={routingIgp?.routingIgp ?? null} selected={activeProtocol} /> : null}
              {activeTab !== 'vlans' && activeCollection ? <nav className="site-intelligence-content-tabs" aria-label={`${activeTab} content`}>
                <button type="button" className={activeSection === 'findings' ? 'is-active' : ''} aria-pressed={activeSection === 'findings'} onClick={() => openCollection(collectionForTab(activeTab, activeProtocol, 'findings')!)}>{activeTab === 'naming' ? 'Families' : 'Findings'}</button>
                <button type="button" className={activeSection === 'questions' ? 'is-active' : ''} aria-pressed={activeSection === 'questions'} onClick={() => openCollection(collectionForTab(activeTab, activeProtocol, 'questions')!)}>Review questions</button>
              </nav> : null}

          {expandedCollection === 'topology' ? <div><TopologyCollection list={topology} loading={collectionLoading === 'topology'} error={collectionError?.collection === 'topology' ? collectionError.message : null} onOpen={(finding) => void openFinding('topology', finding)} onMore={() => topology?.meta.next && void loadCollection('topology', topology.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'topology-review-questions' ? <div><ReviewQuestionCollection title="Topology review questions" list={topologyReviewQuestions} loading={collectionLoading === 'topology-review-questions'} error={collectionError?.collection === 'topology-review-questions' ? collectionError.message : null} onOpen={(finding) => void openFinding('topology', finding)} onMore={() => topologyReviewQuestions?.meta.next && void loadCollection('topology-review-questions', topologyReviewQuestions.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'naming' ? <div><NamingCollection list={naming} loading={collectionLoading === 'naming'} error={collectionError?.collection === 'naming' ? collectionError.message : null} onOpen={(finding) => void openFinding('naming', finding)} onMore={() => naming?.meta.next && void loadCollection('naming', naming.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'naming-review-questions' ? <div><ReviewQuestionCollection title="Naming review questions" list={namingReviewQuestions} loading={collectionLoading === 'naming-review-questions'} error={collectionError?.collection === 'naming-review-questions' ? collectionError.message : null} onOpen={(finding) => void openFinding('naming', finding)} onMore={() => namingReviewQuestions?.meta.next && void loadCollection('naming-review-questions', namingReviewQuestions.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'bgp' ? <div><BgpCollection list={bgpFindings} loading={collectionLoading === 'bgp'} error={collectionError?.collection === 'bgp' ? collectionError.message : null} onOpen={(finding) => void openFinding('bgp', finding)} onMore={() => bgpFindings?.meta.next && void loadCollection('bgp', bgpFindings.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'bgp-review-questions' ? <div><BgpReviewQuestionCollection list={bgpReviewQuestions} loading={collectionLoading === 'bgp-review-questions'} error={collectionError?.collection === 'bgp-review-questions' ? collectionError.message : null} onOpen={(finding) => void openFinding('bgp', finding)} onMore={() => bgpReviewQuestions?.meta.next && void loadCollection('bgp-review-questions', bgpReviewQuestions.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'bfd' ? <div><BfdCollection list={bfd} loading={collectionLoading === 'bfd'} error={collectionError?.collection === 'bfd' ? collectionError.message : null} onOpen={(finding) => void openFinding('bfd', finding)} onMore={() => bfd?.meta.next && void loadCollection('bfd', bfd.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'bfd-review-questions' ? <div><BfdReviewQuestionCollection list={bfdReviewQuestions} loading={collectionLoading === 'bfd-review-questions'} error={collectionError?.collection === 'bfd-review-questions' ? collectionError.message : null} onOpen={(finding) => void openFinding('bfd', finding)} onMore={() => bfdReviewQuestions?.meta.next && void loadCollection('bfd-review-questions', bfdReviewQuestions.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'ospf' ? <div><IgpCollection title="OSPF" list={ospfFindings} loading={collectionLoading === 'ospf'} error={collectionError?.collection === 'ospf' ? collectionError.message : null} onOpen={(finding) => void openFinding('ospf', finding)} onMore={() => ospfFindings?.meta.next && void loadCollection('ospf', ospfFindings.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'ospf-review-questions' ? <div><IgpReviewQuestionCollection title="OSPF" list={ospfReviewQuestions} loading={collectionLoading === 'ospf-review-questions'} error={collectionError?.collection === 'ospf-review-questions' ? collectionError.message : null} onOpen={(finding) => void openFinding('ospf', finding)} onMore={() => ospfReviewQuestions?.meta.next && void loadCollection('ospf-review-questions', ospfReviewQuestions.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'isis' ? <div><IgpCollection title="IS-IS" list={isisFindings} loading={collectionLoading === 'isis'} error={collectionError?.collection === 'isis' ? collectionError.message : null} onOpen={(finding) => void openFinding('isis', finding)} onMore={() => isisFindings?.meta.next && void loadCollection('isis', isisFindings.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'isis-review-questions' ? <div><IgpReviewQuestionCollection title="IS-IS" list={isisReviewQuestions} loading={collectionLoading === 'isis-review-questions'} error={collectionError?.collection === 'isis-review-questions' ? collectionError.message : null} onOpen={(finding) => void openFinding('isis', finding)} onMore={() => isisReviewQuestions?.meta.next && void loadCollection('isis-review-questions', isisReviewQuestions.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          {expandedCollection === 'vlans' ? <div><VlanCollection list={vlans} loading={collectionLoading === 'vlans'} error={collectionError?.collection === 'vlans' ? collectionError.message : null} onOpen={(vlanId) => void openVlan(vlanId)} onBoundaryEvidence={(finding, vlanId) => void openVlanFinding(finding, `VLAN ${vlanId} boundary evidence`)} onMore={() => vlans?.meta.next && void loadCollection('vlans', vlans.meta.next)} onMinimize={minimizeCollection} /></div> : null}
          </>}
          </> : null}
        </div>

      {evidence || evidenceLoading || evidenceError ? <EvidenceDialog title={evidenceTitle} evidence={evidence} loading={evidenceLoading} error={evidenceError} onClose={closeEvidence} /> : null}
      {summary && executive && executiveView ? <ExecutiveDialog executive={executive} summary={summary} routingIgp={routingIgp?.routingIgp ?? null} view={executiveView} onClose={() => setExecutiveView(null)} onOpenSection={(section, sectionType, protocol) => { setExecutiveView(null); selectTab(section, protocol ?? activeProtocol, sectionType); }} /> : null}
      {graphViewer && siteId && summary ? <FullscreenGraphViewer kind={graphViewer} siteId={siteId} generatedAt={summary.snapshot.generatedAt} onClose={closeGraphViewer} /> : null}
    </OrgModuleLayout>
  );
};

/** Displays only resolved-link and source-label observations for one IGP. */
const IgpCard = ({ protocol, title, preview, onCollection, onFinding }: { protocol: 'ospf' | 'isis'; title: string; preview?: SiteIntelligenceOspf | SiteIntelligenceIsis; onCollection: (collection: Collection) => void; onFinding: (finding: FindingReference) => void }) => (
  <section className="site-intelligence-section-card site-intelligence-routing-card">
    <header><div><NetworkIcon size={20} /><div><p>{title} adjacency</p><h3>{preview?.resolvedAdjacencyCount == null ? `No ${title} result in this snapshot` : `${preview.resolvedAdjacencyCount} resolved in-site links`}</h3></div></div><SectionState state={preview?.state ?? 'notMaterialized'} failureCode={preview?.failureCode} /></header>
    {preview?.state === 'available' ? <>
      <section className="site-intelligence-routing-overview"><div><span>Observed overview</span>{preview.summary ? <button type="button" className="ui-button ui-button--ghost" onClick={() => onFinding(preview.summary!)}>Evidence</button> : null}</div><p>{preview.summary?.text ?? `No ${title} overview is available for this snapshot.`}</p></section>
      <p className="site-intelligence-routing-note">Area labels are source reported for resolved links. They do not establish complete peer state or live health.</p>
      <dl className="site-intelligence-inline-stats"><div><dt>{title} instances</dt><dd>{preview.instanceCount ?? 0}</dd></div><div><dt>Review questions</dt><dd>{preview.reviewQuestionCount ?? 0}</dd></div></dl>
      <div className="site-intelligence-routing-actions"><button type="button" className="ui-button ui-button--primary" onClick={() => onCollection(protocol)}>Explore {title} findings</button>{(preview.reviewQuestionCount ?? 0) > 0 ? <button type="button" className="ui-button ui-button--ghost" onClick={() => onCollection(`${protocol}-review-questions`)}>Review questions</button> : null}</div>
      {preview.instancePreview?.length ? <IgpInstancePreviewList title={title} instances={preview.instancePreview} onOpen={onFinding} /> : null}
      {preview.commonalityPreview?.length ? <FindingPreviewList title="Common observed patterns" findings={preview.commonalityPreview} onOpen={onFinding} /> : null}
      {preview.observedDifferencePreview?.length ? <FindingPreviewList title="Observed differences" findings={preview.observedDifferencePreview} onOpen={onFinding} /> : null}
    </> : <SectionUnavailableNotice section={`${title} adjacency`} state={preview?.state ?? 'notMaterialized'} failureCode={preview?.failureCode} />}
  </section>
);

const IgpCollection = ({ title, list, loading, error, onOpen, onMore, onMinimize }: { title: string; list: SiteIntelligenceRoutingFindingListResponse | null; loading: boolean; error?: string | null; onOpen: (finding: FindingReference) => void; onMore: () => void; onMinimize: () => void }) => (
  <CollectionShell title={`${title} instance findings`} description="Snapshot-scoped statements and source labels" loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {list?.state === 'available' ? (list.items.length ? <RoutingFindingList findings={list.items} onOpen={onOpen} /> : <EmptyCollection message={`No ${title} findings were generated for this snapshot.`} />) : list ? <SectionUnavailableNotice section={`${title} adjacency`} state={list.state} failureCode={list.failureCode} /> : <EmptyCollection message={`No ${title} findings were loaded.`} />}
  </CollectionShell>
);

const IgpReviewQuestionCollection = ({ title, list, loading, error, onOpen, onMore, onMinimize }: { title: string; list: SiteIntelligenceRoutingReviewQuestionListResponse | null; loading: boolean; error?: string | null; onOpen: (finding: FindingReference) => void; onMore: () => void; onMinimize: () => void }) => (
  <CollectionShell title={`${title} review questions`} description={reviewQuestionDescription(list?.reviewQuestionCount ?? 0)} loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {list?.state === 'available' ? (list.items.length ? <FindingList findings={list.items} onOpen={onOpen} /> : <EmptyCollection message={`No ${title} review questions were generated.`} />) : list ? <SectionUnavailableNotice section={`${title} review questions`} state={list.state} failureCode={list.failureCode} /> : <EmptyCollection message={`No ${title} review questions were loaded.`} />}
  </CollectionShell>
);

const CollectionShell = ({ title, description, loading, error, children, onMore, hasMore, onMinimize }: { title: string; description: string; loading: boolean; error?: string | null; children: ReactNode; onMore: () => void; hasMore?: boolean; onMinimize: () => void }) => (
  <section className="site-intelligence-collection">
    <header><div><p>{title}</p><h3>{description}</h3></div><button type="button" className="ui-button ui-button--ghost site-intelligence-collection__minimize" onClick={onMinimize}>Back to overview</button></header>
    {loading ? <p className="site-intelligence-loading">Loading…</p> : children}
    {error ? <p className="site-intelligence-error">{error}</p> : null}
    {hasMore ? <button type="button" className="ui-button ui-button--ghost" onClick={onMore}>Load more</button> : null}
  </section>
);

const TopologyCollection = ({ list, loading, error, onOpen, onMore, onMinimize }: { list: SiteIntelligenceTopologyFindingListResponse | null; loading: boolean; error?: string | null; onOpen: (finding: FindingReference) => void; onMore: () => void; onMinimize: () => void }) => (
  <CollectionShell title="Topology findings" description={`${list?.findingCount ?? 0} evidence-backed observations`} loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {(list?.items ?? []).length ? <FindingList findings={list?.items ?? []} onOpen={onOpen} /> : <EmptyCollection message="No topology findings were generated for this snapshot." />}
  </CollectionShell>
);

const BgpCollection = ({ list, loading, error, onOpen, onMore, onMinimize }: { list: SiteIntelligenceRoutingFindingListResponse | null; loading: boolean; error?: string | null; onOpen: (finding: { findingId: string }) => void; onMore: () => void; onMinimize: () => void }) => {
  // The landing card already presents the one site-level overview. This
  // collection intentionally starts with details that add operator value.
  const findings = (list?.items ?? []).filter((finding) => finding.kind !== 'summary');
  return <CollectionShell title="BGP instance and design findings" description="Per-instance observations and cautious design context" loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {list?.state === 'available'
      ? (findings.length ? <RoutingFindingList findings={findings} onOpen={onOpen} /> : <EmptyCollection message="No additional BGP findings were generated for this snapshot." />)
      : list ? <SectionUnavailableNotice section="BGP routing adjacency" state={list.state} failureCode={list.failureCode} /> : <EmptyCollection message="No BGP findings were loaded for this snapshot." />}
  </CollectionShell>;
};

const BgpReviewQuestionCollection = ({ list, loading, error, onOpen, onMore, onMinimize }: { list: SiteIntelligenceRoutingReviewQuestionListResponse | null; loading: boolean; error?: string | null; onOpen: (finding: { findingId: string }) => void; onMore: () => void; onMinimize: () => void }) => (
  <CollectionShell title="Routing review questions" description={reviewQuestionDescription(list?.reviewQuestionCount ?? 0)} loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {list?.state === 'available'
      ? (list.items.length ? <FindingList findings={list.items} onOpen={onOpen} /> : <EmptyCollection message="No routing review questions were generated for this snapshot." />)
      : list ? <SectionUnavailableNotice section="Routing review questions" state={list.state} failureCode={list.failureCode} /> : <EmptyCollection message="No routing review questions were loaded for this snapshot." />}
  </CollectionShell>
);

const BfdCollection = ({ list, loading, error, onOpen, onMore, onMinimize }: { list: SiteIntelligenceRoutingFindingListResponse | null; loading: boolean; error?: string | null; onOpen: (finding: { findingId: string }) => void; onMore: () => void; onMinimize: () => void }) => {
  const findings = (list?.items ?? []).filter((finding) => finding.kind !== 'summary');
  return <CollectionShell title="BFD instance and design findings" description="Snapshot observations and cautious interpretation" loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {list?.state === 'available'
      ? (findings.length ? <RoutingFindingList findings={findings} onOpen={onOpen} /> : <EmptyCollection message="No additional BFD findings were generated for this snapshot." />)
      : list ? <SectionUnavailableNotice section="BFD adjacency" state={list.state} failureCode={list.failureCode} /> : <EmptyCollection message="No BFD findings were loaded for this snapshot." />}
  </CollectionShell>;
};

const BfdReviewQuestionCollection = ({ list, loading, error, onOpen, onMore, onMinimize }: { list: SiteIntelligenceRoutingReviewQuestionListResponse | null; loading: boolean; error?: string | null; onOpen: (finding: { findingId: string }) => void; onMore: () => void; onMinimize: () => void }) => (
  <CollectionShell title="BFD review questions" description={reviewQuestionDescription(list?.reviewQuestionCount ?? 0)} loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {list?.state === 'available'
      ? (list.items.length ? <FindingList findings={list.items} onOpen={onOpen} /> : <EmptyCollection message="No BFD review questions were generated for this snapshot." />)
      : list ? <SectionUnavailableNotice section="BFD review questions" state={list.state} failureCode={list.failureCode} /> : <EmptyCollection message="No BFD review questions were loaded for this snapshot." />}
  </CollectionShell>
);

const NamingCollection = ({ list, loading, error, onOpen, onMore, onMinimize }: { list: SiteIntelligenceNamingListResponse | null; loading: boolean; error?: string | null; onOpen: (finding: FindingReference) => void; onMore: () => void; onMinimize: () => void }) => (
  <CollectionShell title="Naming families" description={`${list?.familyCount ?? 0} observed hostname families`} loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {(list?.items ?? []).length ? <ul className="site-intelligence-row-list">
      {(list?.items ?? []).map((family) => <li key={family.findingId}><div><strong>{family.pattern}</strong><span>{family.interpretation}</span><small>{family.supportCount} devices · {family.variantCount} variants</small></div><button type="button" className="ui-button ui-button--ghost" onClick={() => onOpen({ findingId: family.findingId, text: family.interpretation, confidence: family.confidence })}>Evidence</button></li>)}
    </ul> : <EmptyCollection message="No hostname naming families were generated for this snapshot." />}
  </CollectionShell>
);

const ReviewQuestionCollection = ({ title, list, loading, error, onOpen, onMore, onMinimize }: { title: string; list: SiteIntelligenceReviewQuestionListResponse | null; loading: boolean; error?: string | null; onOpen: (finding: FindingReference) => void; onMore: () => void; onMinimize: () => void }) => (
  <CollectionShell title={title} description={reviewQuestionDescription(list?.reviewQuestionCount ?? 0)} loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {list?.state === 'available'
      ? (list.items.length ? <FindingList findings={list.items} onOpen={onOpen} /> : <EmptyCollection message="No review questions were generated for this snapshot." />)
      : list ? <SectionUnavailableNotice section={title} state={list.state} failureCode={list.failureCode} /> : <EmptyCollection message="No review questions were loaded for this snapshot." />}
  </CollectionShell>
);

const hasVlanBoundarySource = (list: SiteIntelligenceVlanListResponse | null, purpose: string) => Boolean(list?.boundarySources?.some((source) => source.purpose === purpose && source.status === 'present'));

const VlanBoundarySection = ({ summary, list, onEvidence }: {
  summary: SiteIntelligenceSummary;
  list: SiteIntelligenceVlanListResponse | null;
  onEvidence: (finding: { findingId: string }) => void;
}) => {
  const siteReading = list?.boundarySummary ?? summary.sections.vlans.boundarySummary;
  const hasL3Source = (list?.boundarySources ?? summary.sections.vlans.boundarySources ?? []).some((source) => source.purpose === 'l3_membership' && source.status === 'present');
  const showChart = hasL3Source && Boolean(list);
  if (!siteReading && !showChart) return null;
  return <section className="site-intelligence-section-card site-intelligence-boundary-section" aria-labelledby="site-intelligence-boundary-title">
    <header>
      <div><NetworkIcon size={20} /><div><p>Selected L3 graph</p><h3 id="site-intelligence-boundary-title">L2/L3 boundary</h3></div></div>
      {siteReading ? <div className="site-intelligence-boundary-section__actions"><Confidence value={siteReading.confidence} /><button type="button" className="ui-button ui-button--ghost" onClick={() => onEvidence(siteReading)}>View boundary evidence</button></div> : null}
    </header>
    <div className={`site-intelligence-boundary-section__body${siteReading && showChart ? '' : ' site-intelligence-boundary-section__body--single'}`}>
      {siteReading ? <div className="site-intelligence-boundary-section__reading"><p>{siteReading.text}</p><VlanRationale text={siteReading.rationale} /></div> : null}
      {showChart ? <BoundarySnapshotVisual summary={summary} list={list} /> : null}
    </div>
  </section>;
};

const VlanCollection = ({ list, loading, error, onOpen, onBoundaryEvidence, onMore, onMinimize }: { list: SiteIntelligenceVlanListResponse | null; loading: boolean; error?: string | null; onOpen: (vlanId: number) => void; onBoundaryEvidence: (finding: { findingId: string }, vlanId: number) => void; onMore: () => void; onMinimize: () => void }) => (
  <CollectionShell title="Observed VLAN catalog" description={`${list?.vlanCount ?? 0} VLANs in this snapshot`} loading={loading} error={error} onMore={onMore} hasMore={Boolean(list?.meta.next)} onMinimize={onMinimize}>
    {(list?.items ?? []).length ? <div className="site-intelligence-table-wrap">
      <table className="site-intelligence-table">
        <thead><tr><th scope="col">VLAN</th><th scope="col">Observed names</th><th scope="col">Members</th><th scope="col">Name status</th>{hasVlanBoundarySource(list, 'l3_membership') ? <th scope="col">L2/L3 boundary</th> : null}{hasVlanBoundarySource(list, 'fhrp_membership') ? <th scope="col">Configured first hop</th> : null}<th scope="col">Purpose preview</th><th scope="col"><span className="sr-only">Open detail</span></th></tr></thead>
        <tbody>
          {(list?.items ?? []).map((vlan) => <tr key={vlan.vlanId}>
            <th scope="row"><button type="button" className="site-intelligence-vlan-link" onClick={() => onOpen(vlan.vlanId)}>VLAN {vlan.vlanId}</button></th>
            <td>{deviceIds(vlan.observedNames).length ? deviceIds(vlan.observedNames).join(', ') : 'No observed name'}</td>
            <td>{vlan.memberDeviceCount} {vlan.memberDeviceCount === 1 ? 'device' : 'devices'}</td>
            <td><span className={`site-intelligence-name-status site-intelligence-name-status--${vlan.nameStatus}`}>{vlan.nameStatus}</span></td>
            {hasVlanBoundarySource(list, 'l3_membership') ? <td className="site-intelligence-vlan-boundary-cell">
              <span>{vlan.l3BoundaryState === 'observed' ? `${vlan.sviDeviceCount ?? 0} SVI ${(vlan.sviDeviceCount ?? 0) === 1 ? 'device' : 'devices'}` : vlan.l3BoundaryState === 'not_observed' ? 'No SVI resolved' : '—'}</span>
              {vlan.boundaryFindingPreview ? <div className="site-intelligence-vlan-boundary-preview"><p>{vlan.boundaryFindingPreview.text}</p><button type="button" onClick={() => onBoundaryEvidence(vlan.boundaryFindingPreview!, vlan.vlanId)}>Boundary evidence</button></div> : null}
            </td> : null}
            {hasVlanBoundarySource(list, 'fhrp_membership') ? <td>{vlan.fhrpState === 'observed' ? `${vlan.fhrpGroupCount ?? 0} ${(vlan.fhrpGroupCount ?? 0) === 1 ? 'group' : 'groups'}` : vlan.fhrpState === 'not_observed' ? 'No group observed' : null}</td> : null}
            <td>{vlan.purposePreview?.text ?? 'No purpose interpretation'}</td>
            <td><button type="button" className="ui-button ui-button--ghost" onClick={() => onOpen(vlan.vlanId)}>Evidence</button></td>
          </tr>)}
        </tbody>
      </table>
    </div> : <EmptyCollection message="No observed VLANs were generated for this snapshot." />}
  </CollectionShell>
);

const EmptyCollection = ({ message }: { message: string }) => <p className="site-intelligence-empty-state">{message}</p>;

type FindingReference = { findingId: string; text: string; confidence?: string | null };

const FindingList = ({ findings, onOpen }: { findings: FindingReference[]; onOpen: (finding: FindingReference) => void }) => (
  <ul className="site-intelligence-row-list">
    {findings.map((finding) => <li key={finding.findingId}><div><strong>{finding.text}</strong><Confidence value={finding.confidence} /></div><button type="button" className="ui-button ui-button--ghost" onClick={() => onOpen(finding)}>Evidence</button></li>)}
  </ul>
);

const routingFindingKindLabel = (kind: string) => ({
  summary: 'Routing overview',
  instance_summary: 'Instance summary',
  interpretation: 'Design hypothesis',
  commonality: 'Observed commonality',
  observed_difference: 'Observed difference'
}[kind] ?? kind.replaceAll('_', ' '));

const RoutingFindingList = ({ findings, onOpen }: { findings: Array<{ findingId: string; kind: string; instance?: string | null; text: string; confidence?: string | null }>; onOpen: (finding: FindingReference) => void }) => (
  <ul className="site-intelligence-row-list">
    {findings.map((finding) => <li key={finding.findingId}><div><small>{routingFindingKindLabel(finding.kind)}{finding.instance ? ` · ${finding.instance}` : ''}</small><strong>{finding.text}</strong><Confidence value={finding.confidence} /></div><button type="button" className="ui-button ui-button--ghost" onClick={() => onOpen(finding)}>Evidence</button></li>)}
  </ul>
);

const FindingPreviewList = ({ title, findings, onOpen }: { title: string; findings: FindingReference[]; onOpen: (finding: FindingReference) => void }) => (
  <section className="site-intelligence-preview-list">
    <h4>{title}</h4>
    <ul>{findings.map((finding) => <li key={finding.findingId}><span>{finding.text}</span><button type="button" className="ui-button ui-button--ghost" onClick={() => onOpen(finding)}>Evidence</button></li>)}</ul>
  </section>
);

/** Keeps the landing page concise; the complete instance narrative is loaded only when evidence is opened. */
const BgpInstancePreviewList = ({ instances, onOpen }: { instances: SiteIntelligenceRoutingInstancePreview[]; onOpen: (finding: FindingReference) => void }) => (
  <section className="site-intelligence-routing-instances">
    <h4>BGP instances</h4>
    <div className="site-intelligence-routing-table-wrap">
      <table>
        <thead><tr><th scope="col">Instance</th><th scope="col">Visible devices</th><th scope="col">Resolved in-site links</th><th scope="col">Evidence</th></tr></thead>
        <tbody>{instances.map((instance) => <tr key={instance.instance}>
          <th scope="row"><code>{instance.instance}</code></th>
          <td>{instance.visibleDeviceCount}</td>
          <td>{instance.resolvedAdjacencyCount}</td>
          <td>{instance.summary ? <button type="button" className="ui-button ui-button--ghost" aria-label={`Open evidence for ${instance.instance}`} onClick={() => onOpen(instance.summary!)}>Open</button> : null}</td>
        </tr>)}</tbody>
      </table>
    </div>
  </section>
);

const BfdInstancePreviewList = ({ instances, onOpen }: { instances: SiteIntelligenceRoutingInstancePreview[]; onOpen: (finding: FindingReference) => void }) => (
  <section className="site-intelligence-routing-instances">
    <h4>BFD instances</h4>
    <div className="site-intelligence-routing-table-wrap">
      <table>
        <thead><tr><th scope="col">Instance</th><th scope="col">Visible devices</th><th scope="col">Resolved in-site links</th><th scope="col">Evidence</th></tr></thead>
        <tbody>{instances.map((instance) => <tr key={instance.instance}>
          <th scope="row"><code>{instance.instance}</code></th>
          <td>{instance.visibleDeviceCount}</td>
          <td>{instance.resolvedAdjacencyCount}</td>
          <td>{instance.summary ? <button type="button" className="ui-button ui-button--ghost" aria-label={`Open evidence for ${instance.instance}`} onClick={() => onOpen(instance.summary!)}>Open</button> : null}</td>
        </tr>)}</tbody>
      </table>
    </div>
  </section>
);

const IgpInstancePreviewList = ({ title, instances, onOpen }: { title: string; instances: SiteIntelligenceRoutingInstancePreview[]; onOpen: (finding: FindingReference) => void }) => (
  <section className="site-intelligence-routing-instances">
    <h4>{title} instances</h4>
    <div className="site-intelligence-routing-table-wrap"><table>
      <thead><tr><th scope="col">Instance</th><th scope="col">Visible devices</th><th scope="col">Resolved in-site links</th><th scope="col">Evidence</th></tr></thead>
      <tbody>{instances.map((instance) => <tr key={instance.instance}><th scope="row"><code>{instance.instance}</code></th><td>{instance.visibleDeviceCount}</td><td>{instance.resolvedAdjacencyCount}</td><td>{instance.summary ? <button type="button" className="ui-button ui-button--ghost" aria-label={`Open evidence for ${instance.instance}`} onClick={() => onOpen(instance.summary!)}>Open</button> : null}</td></tr>)}</tbody>
    </table></div>
  </section>
);

/** Reuses the Topology Service viewer in a full-width site-scoped dialog. */
const FullscreenGraphViewer = ({ kind, siteId, generatedAt, onClose }: { kind: Exclude<GraphViewer, null>; siteId: string; generatedAt: Date; onClose: () => void }) => {
  const viewId = kind === 'physical' ? 'physical_overview' : 'vlan_membership_summary';
  const title = kind === 'physical' ? 'Physical topology' : 'VLAN membership';
  const dialogRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;
      const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? [])];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => { document.body.style.overflow = previousOverflow; window.removeEventListener('keydown', onKeyDown); previousFocus?.focus(); };
  }, [onClose]);
  return createPortal(<div className="site-intelligence-graph-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="site-intelligence-graph-dialog" role="dialog" aria-modal="true" aria-label={title} ref={dialogRef}>
      <header><div><p>Topology Service view</p><h2>{title}</h2><span>Site Intelligence snapshot generated {formatTimestamp(generatedAt)}. This graph resolves independently and may reflect a different observation time.</span></div><div className="site-intelligence-graph-dialog__actions"><Link className="ui-button ui-button--ghost" to={buildTopologyViewHref(viewId, { scopeType: 'site', scopeId: siteId })}>Open in topology workspace</Link><button type="button" className="ui-button ui-button--primary" onClick={onClose} ref={closeButtonRef}>Close</button></div></header>
      <div className="site-intelligence-graph-dialog__view"><TopologyV2Page embedded hideControls initialSiteId={siteId} forceViewId={viewId} /></div>
    </section>
  </div>, document.body);
};

/** Keeps the site reading short while exposing its selected facts and review prompts on demand. */
const ExecutiveDialog = ({ executive, summary, routingIgp, view, onClose, onOpenSection }: { executive: SiteIntelligenceExecutive; summary: SiteIntelligenceSummary; routingIgp: SiteIntelligenceRoutingIgp | null; view: Exclude<ExecutiveView, null>; onClose: () => void; onOpenSection: (section: ExecutiveSection, sectionType: CollectionSection, protocol?: RoutingProtocol) => void }) => {
  const dialogRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const title = view === 'claims' ? 'Why this summary' : 'Review items';
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
      if (event.key !== 'Tab') return;
      const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])') ?? [])];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => { document.body.style.overflow = previousOverflow; window.removeEventListener('keydown', onKeyDown); previousFocus?.focus(); };
  }, [onClose]);
  return createPortal(<div className="site-intelligence-evidence-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="site-intelligence-executive-dialog" role="dialog" aria-modal="true" aria-labelledby="site-intelligence-executive-dialog-title" ref={dialogRef}>
      <header><div><p>Site reading · selected snapshot</p><h2 id="site-intelligence-executive-dialog-title">{title}</h2></div><button type="button" className="ui-button ui-button--ghost" onClick={onClose} ref={closeButtonRef}>Close</button></header>
      {view === 'claims' ? <>
        <p className="site-intelligence-executive-dialog__intro">These selected observations support the site reading. Open a section for its full snapshot detail.</p>
        <ol className="site-intelligence-executive-dialog__list">{executive.claims.map((claim, index) => <li key={`${claim.section}-${index}`}>
          <div className="site-intelligence-executive-dialog__meta"><span>{isExecutiveSection(claim.section) ? executiveSectionLabels[claim.section] : claim.section}</span><Confidence value={claim.confidence} /></div>
          <strong>{claim.text}</strong>
          {claim.evidence.map((item, evidenceIndex) => <p key={`${item.source}-${evidenceIndex}`}><b>{item.source}:</b> {item.detail}</p>)}
          {isExecutiveSection(claim.section) ? <button type="button" className="ui-button ui-button--ghost" onClick={() => onOpenSection(claim.section as ExecutiveSection, 'findings')}>Open {executiveSectionLabels[claim.section]}</button> : null}
        </li>)}</ol>
      </> : <>
        <p className="site-intelligence-executive-dialog__intro">Questions already raised by the section analyses for this snapshot.</p>
        <ol className="site-intelligence-executive-dialog__list">{executive.reviewItems.map((item, index) => {
          const target = executiveReviewTarget(summary, routingIgp, item);
          return <li key={`${item.section}-${index}`}>
          <div className="site-intelligence-executive-dialog__meta"><span>{isExecutiveSection(item.section) ? executiveSectionLabels[item.section] : item.section}</span></div>
          <strong>{item.text}</strong>
          {target ? <button type="button" className="ui-button ui-button--ghost" onClick={() => onOpenSection(target.tab, target.section, target.protocol)}>{target.label}</button> : null}
        </li>})}</ol>
        {executive.reviewItemCount > executive.reviewItems.length ? <p className="site-intelligence-executive-dialog__intro">Showing {executive.reviewItems.length} of {executive.reviewItemCount} review items. Open a section’s Review questions tab to see more.</p> : null}
      </>}
    </section>
  </div>, document.body);
};

const EvidenceDialog = ({ title, evidence, loading, error, onClose }: { title: string; evidence: EvidenceSelection | null; loading: string | null; error: string | null; onClose: () => void }) => {
  const dialogRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
      if (event.key !== 'Tab') return;
      const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? [])];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => { document.body.style.overflow = previousOverflow; window.removeEventListener('keydown', onKeyDown); previousFocus?.focus(); };
  }, [onClose]);
  return createPortal(
    <div className="site-intelligence-evidence-backdrop" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="site-intelligence-evidence-page" role="dialog" aria-modal="true" aria-label={title} ref={dialogRef}>
        <header><div><p>Selected snapshot evidence</p><h3>{title}</h3></div><button type="button" className="ui-button ui-button--ghost" onClick={onClose} ref={closeButtonRef}>Close evidence</button></header>
        {loading ? <p className="site-intelligence-loading">{loading}</p> : null}
        {error ? <p className="site-intelligence-error">{error}</p> : null}
        {evidence?.kind === 'topology' ? <TopologyEvidence data={evidence.data} architectureBestFit={evidence.architectureBestFit} summary={evidence.summary} summaryUnavailable={evidence.summaryUnavailable} /> : null}
        {evidence?.kind === 'naming' ? <NamingEvidence data={evidence.data} /> : null}
        {evidence?.kind === 'bgp' ? <BgpEvidence data={evidence.data} /> : null}
        {evidence?.kind === 'bfd' ? <BfdEvidence data={evidence.data} /> : null}
        {evidence?.kind === 'ospf' ? <IgpEvidence title="OSPF" data={evidence.data} /> : null}
        {evidence?.kind === 'isis' ? <IgpEvidence title="IS-IS" data={evidence.data} /> : null}
        {evidence?.kind === 'vlan-finding' ? <VlanFindingEvidence data={evidence.data} /> : null}
        {evidence?.kind === 'vlan' ? <VlanEvidence data={evidence.data} /> : null}
      </section>
    </div>, document.body
  );
};

const deviceFor = (devices: SiteIntelligenceDevice[], deviceId: string) => devices.find((device) => device.id === deviceId);
/** Normalizes generated unique collections and JSON-array responses for rendering. */
const deviceIds = (values: Iterable<string>) => [...values];
const uniqueStrings = (values: Iterable<string>) => [...new Set(values)];
const uniqueBy = <T,>(values: Iterable<T>, key: (value: T) => string): T[] => {
  const unique = new Map<string, T>();
  for (const value of values) unique.set(key(value), value);
  return [...unique.values()];
};

/** Renders a public device reference without exposing its UUID as page text. */
const DeviceLink = ({ devices, deviceId }: { devices: SiteIntelligenceDevice[]; deviceId: string }) => {
  const device = deviceFor(devices, deviceId);
  const { siteId } = useParams<{ siteId: string }>();
  const orgId = useActiveOrgId();
  if (!device?.hostname) return <>Unknown device</>;
  const params = new URLSearchParams({ deviceId: device.id });
  if (siteId) params.set('siteId', siteId);
  if (orgId) params.set('orgId', orgId);
  return <Link className="site-intelligence-device-link" title={`Open ${device.hostname} device details`} to={`/app/inventory?${params.toString()}`}>{device.hostname}</Link>;
};

const DeviceLinks = ({ devices, deviceIds: ids }: { devices: SiteIntelligenceDevice[]; deviceIds: Iterable<string> }) => <>
  {deviceIds(ids).map((deviceId, index, allDeviceIds) => <span key={deviceId}><DeviceLink devices={devices} deviceId={deviceId} />{index < allDeviceIds.length - 1 ? ', ' : null}</span>)}
</>;

/** Displays each observed device as a compact hostname and inventory-role chip. */
const DeviceChipList = ({ devices, deviceIds: ids, showRole = true }: { devices: SiteIntelligenceDevice[]; deviceIds: Iterable<string>; showRole?: boolean }) => (
  <ul className="site-intelligence-device-chip-list">
    {deviceIds(ids).map((deviceId) => {
      const device = deviceFor(devices, deviceId);
      return <li key={deviceId}><DeviceLink devices={devices} deviceId={deviceId} />{showRole && device?.storedRole ? <span>{device.storedRole}</span> : null}</li>;
    })}
  </ul>
);

const EvidenceGroup = ({ title, children }: { title: string; children: ReactNode }) => <section className="site-intelligence-evidence-group"><h3>{title}</h3>{children}</section>;

type TopologySubjectRoleGroup = {
  role: string;
  deviceIds: string[];
};

/** Groups exact finding subjects by their inventory-backed role for quick topology scanning. */
const topologySubjectRoleGroups = (devices: SiteIntelligenceDevice[], deviceIds: Iterable<string>): TopologySubjectRoleGroup[] => {
  const groups = new Map<string, string[]>();
  for (const deviceId of deviceIds) {
    const role = deviceFor(devices, deviceId)?.storedRole?.trim() || 'No stored role';
    const group = groups.get(role) ?? [];
    group.push(deviceId);
    groups.set(role, group);
  }
  return [...groups.entries()]
    .map(([role, groupedDeviceIDs]) => ({ role, deviceIds: groupedDeviceIDs }))
    .sort((left, right) => {
      if (left.role === 'No stored role') return 1;
      if (right.role === 'No stored role') return -1;
      return left.role.localeCompare(right.role);
    });
};

const TopologySubjectTable = ({ devices, deviceIds, caption }: { devices: SiteIntelligenceDevice[]; deviceIds: Iterable<string>; caption: string }) => {
  const groups = topologySubjectRoleGroups(devices, deviceIds);
  return <div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table site-intelligence-subject-table">
    <caption className="sr-only">{caption}</caption>
    <thead><tr><th scope="col">Stored role</th><th scope="col">Observed devices</th><th scope="col">Count</th></tr></thead>
    <tbody>{groups.map((group) => <tr key={group.role}><th scope="row">{group.role}</th><td><DeviceChipList devices={devices} deviceIds={group.deviceIds} showRole={false} /></td><td>{group.deviceIds.length}</td></tr>)}</tbody>
  </table></div>;
};

const TopologyEvidence = ({ data, architectureBestFit, summary, summaryUnavailable }: { data: SiteIntelligenceTopologyFindingDetailResponse; architectureBestFit?: string; summary?: SiteIntelligenceTopologyFindingDetailResponse; summaryUnavailable?: boolean }) => {
  const finding = data.finding;
  // A topology summary can cite observations outside the architecture decision
  // (for example isolated firewalls). Its prose belongs in this evidence, but its
  // evidence must not be presented as an architectural layer.
  const devices = data.devices;
  const physicalLinks = uniqueBy(data.finding.evidence.physicalLinks, (link) => [...[link.fromDeviceId, link.toDeviceId].sort(), String(link.linkCount)].join(':'));
  const haPairs = uniqueBy(data.finding.evidence.haPairs, (pair) => deviceIds(pair.deviceIds).sort().join(':'));
  const components = uniqueBy(data.finding.evidence.components, (component) => deviceIds(component.deviceIds).sort().join(':'));
  const statementTitle = architectureBestFit ? 'Architecture classification' : 'Topology finding';
  const statementText = architectureBestFit ? `${architectureLabel(architectureBestFit)} architecture` : finding.text;
  const subjectDeviceIds = uniqueStrings(deviceIds(finding.subjectDeviceIds));
  const architectureLayerDeviceIds = architectureBestFit
    ? uniqueStrings([...subjectDeviceIds, ...physicalLinks.flatMap((link) => [link.fromDeviceId, link.toDeviceId])])
    : subjectDeviceIds;
  const singleSubjectID = subjectDeviceIds.length === 1 ? subjectDeviceIds[0] : null;
  const hasDistinctComponentEvidence = components.some((component) => deviceIds(component.deviceIds).some((deviceID) => !subjectDeviceIds.includes(deviceID)));
  const showComponents = components.length > 0 && (physicalLinks.length > 0 || haPairs.length > 0 || hasDistinctComponentEvidence);
  return <div className="site-intelligence-evidence">
    <section className="site-intelligence-interpretation-card">
      <span>{statementTitle}</span>
      <p>{statementText}</p>
      <Confidence value={finding.confidence} />
      {singleSubjectID ? <div className="site-intelligence-interpretation-card__subject"><span>Applies to</span><DeviceLink devices={devices} deviceId={singleSubjectID} /></div> : null}
    </section>
    {summary ? <section className="site-intelligence-interpretation-card">
      <span>Topology summary</span>
      <p>{summary.finding.text}</p>
    </section> : null}
    {summaryUnavailable ? <p className="site-intelligence-section-notice">Topology summary evidence is unavailable; architecture evidence remains available below.</p> : null}
    <dl className="site-intelligence-evidence-stats">
      <div><dt>{architectureBestFit ? 'Architecture devices' : 'Subjects'}</dt><dd>{architectureLayerDeviceIds.length}</dd></div>
      <div><dt>Physical links</dt><dd>{physicalLinks.length}</dd></div>
      <div><dt>HA pairs</dt><dd>{haPairs.length}</dd></div>
      <div><dt>Components</dt><dd>{components.length}</dd></div>
    </dl>
    {architectureLayerDeviceIds.length ? <EvidenceGroup title={architectureBestFit ? 'Architecture layers observed' : 'Observed subjects'}><TopologySubjectTable devices={devices} deviceIds={architectureLayerDeviceIds} caption={architectureBestFit ? 'Devices participating in the architecture evidence, grouped by stored role' : 'Devices that are the subject of this topology finding, grouped by stored role'} /></EvidenceGroup> : null}
    {physicalLinks.length ? <EvidenceGroup title="Observed physical connections"><div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table">
      <caption className="sr-only">Physical device-to-device connections supporting this topology finding</caption>
      <thead><tr><th scope="col">Device A</th><th scope="col">Relationship</th><th scope="col">Device B</th><th scope="col">Connections</th></tr></thead>
      <tbody>{physicalLinks.map((link) => <tr key={`${link.fromDeviceId}-${link.toDeviceId}`}><td><DeviceLink devices={devices} deviceId={link.fromDeviceId} /></td><td className="site-intelligence-relationship-symbol" aria-label="directly connected to">↔</td><td><DeviceLink devices={devices} deviceId={link.toDeviceId} /></td><td>{link.linkCount}</td></tr>)}</tbody>
    </table></div></EvidenceGroup> : null}
    {haPairs.length ? <EvidenceGroup title="Observed HA relationships"><div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table">
      <caption className="sr-only">High-availability device pairs supporting this topology finding</caption>
      <thead><tr><th scope="col">Device A</th><th scope="col">Relationship</th><th scope="col">Device B</th></tr></thead>
      <tbody>{haPairs.map((pair) => {
        const pairDeviceIds = deviceIds(pair.deviceIds);
        return <tr key={pairDeviceIds.join('-')}><td><DeviceLink devices={devices} deviceId={pairDeviceIds[0]} /></td><td className="site-intelligence-relationship-symbol" aria-label="high availability pair with">⇄</td><td><DeviceLink devices={devices} deviceId={pairDeviceIds[1]} /></td></tr>;
      })}</tbody>
    </table></div></EvidenceGroup> : null}
    {showComponents ? <EvidenceGroup title="Observed connected components"><div className="site-intelligence-component-list">{components.map((component, index) => {
      const componentDeviceIds = deviceIds(component.deviceIds);
      return <details key={componentDeviceIds.join('-')}><summary><strong>Component {index + 1}</strong><span>{componentDeviceIds.length} {componentDeviceIds.length === 1 ? 'device' : 'devices'}</span></summary><DeviceChipList devices={devices} deviceIds={component.deviceIds} /></details>;
    })}</div></EvidenceGroup> : null}
  </div>;
};

const bgpObservationLabels: Record<string, string> = {
  resolved_bgp_fabric: 'Resolved BGP fabric',
  session_type_pattern: 'BGP session type pattern',
  address_family_pattern: 'BGP address-family pattern',
  paired_bgp_asn_relationship_pattern: 'Local and remote ASN pairing pattern',
  bgp_hold_time_rollup_coverage_incomplete: 'Hold-timer rollup coverage incomplete',
  bgp_negotiated_version_rollup_coverage_incomplete: 'Negotiated-version rollup coverage incomplete',
  instance_resolved_adjacency_coverage: 'Instance resolved-adjacency coverage',
  instance_session_type_pattern: 'Instance BGP session type pattern',
  instance_bgp_hold_time_rollup_coverage_incomplete: 'Instance hold-timer rollup coverage incomplete',
  instance_without_resolved_adjacency: 'Instance without resolved in-site adjacency',
  shared_stored_role: 'Shared stored role',
  instance_uniform_bgp_negotiated_version_rollup: 'Uniform negotiated BGP version rollup',
  repeated_dual_homing: 'Repeated dual-homing pattern'
};

const bgpObservationLabel = (kind: string) => bgpObservationLabels[kind] ?? kind.replaceAll('_', ' ');

const bgpValueLabel = (value: string) => ({
  ebgp: 'eBGP',
  ibgp: 'iBGP',
  ipv4_unicast: 'IPv4 unicast',
  ipv6_unicast: 'IPv6 unicast',
  distinct_local_remote_as: 'distinct local and remote ASNs'
}[value] ?? value);

const bgpObservationScope = (observation: SiteIntelligenceBgpObservation): string => {
  const scope: string[] = [];
  if (observation.instance) scope.push(observation.instance);
  if (observation.role) scope.push(`Stored role: ${observation.role}`);
  if (observation.metric) scope.push(observation.metric.replaceAll('_', ' '));
  return scope.join(' · ') || 'Site-wide';
};

/**
 * Formats observation counts using their source contract. Instance coverage uses
 * observedCount for visible device nodes; all other observations use it for the
 * population that supplied the stated routing fact.
 */
const bgpObservationCoverage = (observation: SiteIntelligenceBgpObservation): string => {
  const counts: Array<[string, number | undefined]> = [
    ['Eligible', observation.eligibleCount],
    [
      observation.kind === 'instance_resolved_adjacency_coverage' || observation.kind === 'instance_without_resolved_adjacency'
        ? 'Visible devices'
        : 'Observed',
      observation.observedCount
    ],
    ['Matching', observation.matchingCount],
    ['Exceptions', observation.exceptionCount]
  ];
  return counts.filter(([, value]) => value !== undefined).map(([label, value]) => `${label}: ${value}`).join(' · ') || '—';
};

/** Formats the public scope fields that accompany one operator-facing BGP fact. */
const bgpOperatorEvidenceScope = (evidence: SiteIntelligenceBgpOperatorEvidence): string => {
  const scope: string[] = [];
  if (evidence.instance) scope.push(evidence.instance);
  if (evidence.role) scope.push(`Stored role: ${evidence.role}`);
  if (evidence.metric) scope.push(evidence.metric.replaceAll('_', ' '));
  return scope.join(' · ') || 'Site-wide';
};

const bgpOperatorEvidenceStatusLabel = (status: SiteIntelligenceBgpOperatorEvidence['status']): string => ({
  complete: 'Complete',
  incomplete: 'Incomplete coverage',
  variation: 'Variation',
  attention: 'Attention',
  informational: 'Informational'
}[status]);

const routingFindingTitle = (finding: { kind: string; category?: string | null }, protocol: 'BGP' | 'BFD' = 'BGP') => {
  if (finding.kind === 'review_question') return 'Review question';
  if (finding.category === 'design_hypothesis') return 'Cautious design hypothesis';
  return finding.kind === 'summary' && protocol === 'BFD' ? 'BFD overview' : routingFindingKindLabel(finding.kind);
};

const BgpEvidence = ({ data }: { data: SiteIntelligenceBgpFindingDetailResponse }) => {
  const observations = data.finding.evidence.observations;
  const operatorEvidence = data.finding.evidence.operatorEvidence ?? [];
  const evidenceFacts = operatorEvidence.length ? operatorEvidence : observations;
  const observedDeviceIds = uniqueStrings(evidenceFacts.flatMap((fact) => deviceIds(fact.deviceIds)));
  const peerDeviceIds = uniqueStrings(evidenceFacts.flatMap((fact) => deviceIds(fact.peerDeviceIds)));
  return <div className="site-intelligence-evidence">
    <section className="site-intelligence-interpretation-card">
      <span>{routingFindingTitle(data.finding)}</span>
      <p>{data.finding.text}</p>
      <Confidence value={data.finding.confidence} />
    </section>
    <dl className="site-intelligence-evidence-stats">
      <div><dt>BGP facts</dt><dd>{evidenceFacts.length}</dd></div>
      <div><dt>Devices in evidence</dt><dd>{observedDeviceIds.length}</dd></div>
      <div><dt>Peer devices</dt><dd>{peerDeviceIds.length}</dd></div>
    </dl>
    <EvidenceGroup title="BGP facts">
      {operatorEvidence.length ? <div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table site-intelligence-routing-observations-table">
        <caption className="sr-only">Operator-facing BGP facts supporting this finding</caption>
        <thead><tr><th scope="col">BGP fact</th><th scope="col">Instance or scope</th><th scope="col">Values</th><th scope="col">Measures</th><th scope="col">Status</th></tr></thead>
        <tbody>{operatorEvidence.map((evidence, index) => {
          const values = deviceIds(evidence.values);
          return <tr key={`${evidence.type}:${evidence.instance ?? 'site'}:${index}`}>
            <th scope="row">{evidence.title}</th>
            <td>{bgpOperatorEvidenceScope(evidence)}</td>
            <td>{values.length ? values.map(bgpValueLabel).join(', ') : '—'}</td>
            <td>{evidence.measures.length ? evidence.measures.map((measure) => `${measure.label}: ${measure.value}`).join(' · ') : '—'}</td>
            <td>{bgpOperatorEvidenceStatusLabel(evidence.status)}</td>
          </tr>;
        })}</tbody>
      </table></div> : observations.length ? <div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table site-intelligence-routing-observations-table">
        <caption className="sr-only">Deterministic BGP observations supporting this finding</caption>
        <thead><tr><th scope="col">Observation</th><th scope="col">Instance or scope</th><th scope="col">Values</th><th scope="col">Coverage</th><th scope="col">Resolved in-site links</th><th scope="col">Confidence</th></tr></thead>
        <tbody>{observations.map((observation, index) => {
          const values = deviceIds(observation.values);
          return <tr key={`${observation.kind}:${observation.instance ?? 'site'}:${index}`}>
            <th scope="row">{bgpObservationLabel(observation.kind)}</th>
            <td>{bgpObservationScope(observation)}</td>
            <td>{values.length ? values.map(bgpValueLabel).join(', ') : '—'}</td>
            <td>{bgpObservationCoverage(observation)}</td>
            <td>{observation.adjacencyCount ?? '—'}</td>
            <td><Confidence value={observation.maxConfidence} /></td>
          </tr>;
        })}</tbody>
      </table></div> : <p>No deterministic observations were supplied for this finding.</p>}
    </EvidenceGroup>
    {observedDeviceIds.length ? <EvidenceGroup title="Observed devices"><DeviceChipList devices={data.devices} deviceIds={observedDeviceIds} /></EvidenceGroup> : null}
    {peerDeviceIds.length ? <EvidenceGroup title="Observed peer devices"><DeviceChipList devices={data.devices} deviceIds={peerDeviceIds} /></EvidenceGroup> : null}
  </div>;
};

const bfdObservationLabels: Record<string, string> = {
  resolved_bfd_adjacency_coverage: 'Resolved BFD links',
  instance_resolved_bfd_adjacency_coverage: 'BFD instance coverage',
  bfd_session_state_coverage_incomplete: 'Session-state coverage incomplete',
  bfd_session_state_pattern: 'Source-reported session-state pattern',
  bfd_session_state_variation: 'Source-reported session-state variation',
  bfd_local_interface_coverage_incomplete: 'Local-interface coverage incomplete',
  bfd_role_pair_coverage_incomplete: 'Stored-role pair coverage incomplete',
  bfd_role_pair_pattern: 'Stored-role pair pattern',
  bfd_role_pair_variation: 'Stored-role pair variation',
  bfd_repeated_peer_attachment: 'Repeated peer attachment'
};

/** Keeps BFD link counts separate from device counts in instance evidence. */
const bfdObservationMeasures = (observation: SiteIntelligenceBfdObservation): string => {
  if (observation.kind === 'instance_resolved_bfd_adjacency_coverage') {
    return `Visible devices: ${observation.observedCount ?? '—'} · Resolved links: ${observation.adjacencyCount ?? '—'}`;
  }
  if (observation.kind === 'resolved_bfd_adjacency_coverage') {
    return `Resolved links: ${observation.observedCount ?? observation.adjacencyCount ?? '—'}`;
  }
  if (observation.kind === 'bfd_repeated_peer_attachment') {
    return `Devices with shared peers: ${observation.observedCount ?? '—'} · Shared peer devices: ${deviceIds(observation.peerDeviceIds).length}`;
  }
  const counts: Array<[string, number | null | undefined]> = [
    ['Resolved links considered', observation.eligibleCount],
    ['Links with data', observation.observedCount],
    ['Links with most common value', observation.matchingCount],
    [observation.kind === 'bfd_session_state_variation' || observation.kind === 'bfd_role_pair_variation' ? 'Links differing from majority' : 'Links missing data', observation.exceptionCount]
  ];
  return counts.filter(([, value]) => value != null).map(([label, value]) => `${label}: ${value}`).join(' · ') || '—';
};

const BfdEvidence = ({ data }: { data: SiteIntelligenceBfdFindingDetailResponse }) => {
  const observations = data.finding.evidence.observations;
  const observedDeviceIds = uniqueStrings(observations.flatMap((observation) => deviceIds(observation.deviceIds)));
  const peerDeviceIds = uniqueStrings(observations.flatMap((observation) => deviceIds(observation.peerDeviceIds)));
  return <div className="site-intelligence-evidence">
    <section className="site-intelligence-interpretation-card">
      <span>{routingFindingTitle(data.finding, 'BFD')}</span>
      <p>{data.finding.text}</p>
      <Confidence value={data.finding.confidence} />
    </section>
    <p className="site-intelligence-routing-evidence-note">Source-reported BFD state reflects the selected snapshot. It does not establish current link health.</p>
    <dl className="site-intelligence-evidence-stats">
      <div><dt>BFD facts</dt><dd>{observations.length}</dd></div>
      <div><dt>Devices in evidence</dt><dd>{observedDeviceIds.length}</dd></div>
      <div><dt>Peer devices</dt><dd>{peerDeviceIds.length}</dd></div>
    </dl>
    <EvidenceGroup title="BFD facts">
      {observations.length ? <p className="site-intelligence-routing-observations__hint">Scroll sideways to view all columns.</p> : null}
      {observations.length ? <div className="site-intelligence-table-wrap site-intelligence-routing-observations" role="region" aria-label="BFD facts table" tabIndex={0}><table className="site-intelligence-evidence-table site-intelligence-routing-observations-table">
        <caption className="sr-only">Deterministic BFD observations supporting this finding</caption>
        <thead><tr><th scope="col">BFD fact</th><th scope="col">Instance or scope</th><th scope="col">Values</th><th scope="col">Measures</th><th scope="col">Confidence</th></tr></thead>
        <tbody>{observations.map((observation, index) => <tr key={`${observation.kind}:${observation.instance ?? 'site'}:${index}`}>
          <th scope="row">{bfdObservationLabels[observation.kind] ?? observation.kind.replaceAll('_', ' ')}</th>
          <td>{[observation.instance, observation.role ? `Stored role: ${observation.role}` : null].filter(Boolean).join(' · ') || 'Site-wide'}</td>
          <td>{deviceIds(observation.values).length ? deviceIds(observation.values).join(', ') : '—'}</td>
          <td>{bfdObservationMeasures(observation)}</td>
          <td><Confidence value={observation.maxConfidence} /></td>
        </tr>)}</tbody>
      </table></div> : <p>No deterministic BFD observations were supplied for this finding.</p>}
    </EvidenceGroup>
    {observedDeviceIds.length ? <EvidenceGroup title="Observed devices"><DeviceChipList devices={data.devices} deviceIds={observedDeviceIds} /></EvidenceGroup> : null}
    {peerDeviceIds.length ? <EvidenceGroup title="Observed peer devices"><DeviceChipList devices={data.devices} deviceIds={peerDeviceIds} /></EvidenceGroup> : null}
  </div>;
};

const igpObservationLabels: Record<string, string> = {
  resolved_igp_adjacency_coverage: 'Resolved in-site links',
  instance_resolved_igp_adjacency_coverage: 'Instance link and device coverage',
  igp_area_coverage_incomplete: 'Source area labels missing',
  igp_reported_area_pattern: 'Source-reported area pattern',
  igp_reported_area_variation: 'Source-reported area variation'
};

const igpObservationMeasures = (observation: SiteIntelligenceOspfObservation | SiteIntelligenceIsisObservation) => {
  if (observation.kind === 'resolved_igp_adjacency_coverage') return `Resolved links: ${observation.observedCount ?? observation.adjacencyCount ?? '—'}`;
  if (observation.kind === 'instance_resolved_igp_adjacency_coverage') return `Visible devices: ${observation.observedCount ?? '—'} · Resolved links: ${observation.adjacencyCount ?? '—'}`;
  return [
    ['Resolved links considered', observation.eligibleCount],
    ['Links with area label', observation.observedCount],
    ['Links sharing most common label', observation.matchingCount],
    ['Links missing or differing', observation.exceptionCount]
  ].filter(([, value]) => value != null).map(([label, value]) => `${label}: ${value}`).join(' · ') || '—';
};

const IgpEvidence = ({ title, data }: { title: string; data: SiteIntelligenceOspfFindingDetailResponse | SiteIntelligenceIsisFindingDetailResponse }) => {
  const observations = data.finding.evidence.observations;
  const observedDeviceIds = uniqueStrings(observations.flatMap((observation) => deviceIds(observation.deviceIds)));
  return <div className="site-intelligence-evidence">
    <section className="site-intelligence-interpretation-card"><span>{data.finding.kind === 'summary' ? `${title} overview` : routingFindingKindLabel(data.finding.kind)}</span><p>{data.finding.text}</p><Confidence value={data.finding.confidence} /></section>
    <p className="site-intelligence-routing-evidence-note">Area labels are source reported for retained links. They do not establish endpoint agreement, complete peer state, or live health.</p>
    <dl className="site-intelligence-evidence-stats"><div><dt>{title} facts</dt><dd>{observations.length}</dd></div><div><dt>Devices in evidence</dt><dd>{observedDeviceIds.length}</dd></div></dl>
    <EvidenceGroup title={`${title} facts`}>
      {observations.length ? <p className="site-intelligence-routing-observations__hint">Scroll sideways to view all columns.</p> : null}
      {observations.length ? <div className="site-intelligence-table-wrap site-intelligence-routing-observations" role="region" aria-label={`${title} facts table`} tabIndex={0}><table className="site-intelligence-evidence-table site-intelligence-routing-observations-table"><caption className="sr-only">Deterministic {title} observations supporting this finding</caption><thead><tr><th scope="col">Fact</th><th scope="col">Instance or scope</th><th scope="col">Area labels</th><th scope="col">Measures</th><th scope="col">Confidence</th></tr></thead><tbody>{observations.map((observation, index) => <tr key={`${observation.kind}:${observation.instance ?? 'site'}:${index}`}><th scope="row">{igpObservationLabels[observation.kind] ?? observation.kind.replaceAll('_', ' ')}</th><td>{observation.instance ?? 'Site-wide'}</td><td>{deviceIds(observation.values).length ? deviceIds(observation.values).join(', ') : '—'}</td><td>{igpObservationMeasures(observation)}</td><td><Confidence value={observation.maxConfidence} /></td></tr>)}</tbody></table></div> : <p>No deterministic {title} observations were supplied for this finding.</p>}
    </EvidenceGroup>
    {observedDeviceIds.length ? <EvidenceGroup title="Observed devices"><DeviceChipList devices={data.devices} deviceIds={observedDeviceIds} /></EvidenceGroup> : null}
  </div>;
};

type NamingEvidenceRow = {
  deviceId: string;
  familyPattern?: string;
  variantPattern?: string;
  explainedBy: string[];
};

type NamingVariantInterpretation = {
  familyPattern: string;
  variantPattern: string;
  interpretation: string;
};

/** Converts nested naming-family evidence into one readable row per supporting device. */
const namingEvidenceRows = (data: SiteIntelligenceNamingFindingDetailResponse): NamingEvidenceRow[] => {
  const rows = new Map<string, NamingEvidenceRow>();
  const representedDeviceIds = new Set<string>();
  for (const family of data.finding.evidence.families) {
    for (const deviceId of family.deviceIds) {
      const variant = family.variants.find((candidate) => deviceIds(candidate.deviceIds).includes(deviceId));
      rows.set(`${family.pattern}:${deviceId}`, {
        deviceId,
        familyPattern: family.pattern,
        variantPattern: variant?.pattern,
        explainedBy: variant ? deviceIds(variant.explainedBy) : []
      });
      representedDeviceIds.add(deviceId);
    }
  }
  for (const deviceId of data.finding.evidence.supportingDeviceIds) {
    if (!representedDeviceIds.has(deviceId)) rows.set(`supporting:${deviceId}`, { deviceId, explainedBy: [] });
  }
  return [...rows.values()].sort((left, right) => {
    const leftName = deviceFor(data.devices, left.deviceId)?.hostname ?? left.deviceId;
    const rightName = deviceFor(data.devices, right.deviceId)?.hostname ?? right.deviceId;
    return leftName.localeCompare(rightName);
  });
};

/** Returns each supplied variant interpretation once, independent of device count. */
const namingVariantInterpretations = (data: SiteIntelligenceNamingFindingDetailResponse): NamingVariantInterpretation[] => {
  const interpretations = new Map<string, NamingVariantInterpretation>();
  for (const family of data.finding.evidence.families) {
    for (const variant of family.variants) {
      interpretations.set(`${family.pattern}:${variant.pattern}`, {
        familyPattern: family.pattern,
        variantPattern: variant.pattern,
        interpretation: variant.interpretation
      });
    }
  }
  return [...interpretations.values()].sort((left, right) => left.variantPattern.localeCompare(right.variantPattern));
};

const NamingEvidence = ({ data }: { data: SiteIntelligenceNamingFindingDetailResponse }) => {
  const rows = namingEvidenceRows(data);
  const interpretations = namingVariantInterpretations(data);
  return <div className="site-intelligence-evidence">
    <p className="site-intelligence-evidence__statement">{data.finding.text}</p>
    <Confidence value={data.finding.confidence} />
    {interpretations.length ? <EvidenceGroup title="Naming pattern interpretations"><ul className="site-intelligence-interpretation-list">{interpretations.map((interpretation) => <li key={`${interpretation.familyPattern}:${interpretation.variantPattern}`}><strong>{interpretation.variantPattern}</strong><p>{interpretation.interpretation}</p></li>)}</ul></EvidenceGroup> : null}
    <EvidenceGroup title="Supporting devices">
      {rows.length ? <div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table">
        <caption className="sr-only">Devices supporting this hostname naming finding</caption>
        <thead><tr><th scope="col">Device</th><th scope="col">Stored role</th><th scope="col">Hostname family</th><th scope="col">Observed variant</th><th scope="col">Explained by</th></tr></thead>
        <tbody>{rows.map((row) => {
          const device = deviceFor(data.devices, row.deviceId);
          return <tr key={`${row.familyPattern ?? 'supporting'}:${row.deviceId}`}><td><DeviceLink devices={data.devices} deviceId={row.deviceId} /></td><td>{device?.storedRole ?? '—'}</td><td>{row.familyPattern ?? '—'}</td><td>{row.variantPattern ?? '—'}</td><td>{row.explainedBy.length ? row.explainedBy.join(', ') : '—'}</td></tr>;
        })}</tbody>
      </table></div> : <p>No supporting device evidence was supplied.</p>}
    </EvidenceGroup>
  </div>;
};

type VlanMemberDeviceGroup = {
  deviceId: string;
  interfaceNames: string[];
};

/** Groups individual interface memberships under their reporting device without losing interface evidence. */
const vlanMemberDeviceGroups = (data: SiteIntelligenceVlanDetailResponse): VlanMemberDeviceGroup[] => {
  const grouped = new Map<string, Set<string>>();
  for (const member of data.vlan.members) {
    const interfaces = grouped.get(member.deviceId) ?? new Set<string>();
    interfaces.add(member.interfaceName);
    grouped.set(member.deviceId, interfaces);
  }
  return [...grouped.entries()]
    .map(([deviceId, interfaceNames]) => ({ deviceId, interfaceNames: [...interfaceNames].sort((left, right) => left.localeCompare(right)) }))
    .sort((left, right) => (deviceFor(data.devices, left.deviceId)?.hostname ?? left.deviceId).localeCompare(deviceFor(data.devices, right.deviceId)?.hostname ?? right.deviceId));
};

const InterfaceChipList = ({ interfaceNames }: { interfaceNames: string[] }) => <ul className="site-intelligence-interface-chip-list">
  {interfaceNames.map((interfaceName) => <li key={interfaceName}>{interfaceName}</li>)}
</ul>;

const VlanRationale = ({ text }: { text?: string }) => text ? <p className="site-intelligence-vlan-rationale"><strong>Why we think this:</strong> {text}</p> : null;

const VlanFindingList = ({ findings }: { findings: SiteIntelligenceVlanFinding[] }) => <ul className="site-intelligence-finding-list">
  {findings.map((finding) => <li key={finding.findingId}><div><p>{finding.text}</p><VlanRationale text={finding.rationale} /></div><Confidence value={finding.confidence} /></li>)}
</ul>;

const VlanBoundaryEvidence = ({ data }: { data: Pick<SiteIntelligenceVlanDetailResponse, 'vlan' | 'devices' | 'boundarySources' | 'boundaryFindings'> }) => {
  const boundary = data.vlan.l3Boundary;
  const l3Source = data.boundarySources?.find((source) => source.purpose === 'l3_membership' && source.status === 'present');
  const fhrpSource = data.boundarySources?.find((source) => source.purpose === 'fhrp_membership' && source.status === 'present');
  if (!boundary || !l3Source) return null;
  return <>
    {boundary.state === 'observed' && boundary.svis.length ? <EvidenceGroup title="Observed L2/L3 boundary">
      <p>These configured SVIs connect this VLAN to Layer 3 on the selected graph.</p>
      <div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table">
        <caption className="sr-only">Observed VLAN SVIs by device, VRF, and subnet</caption>
        <thead><tr><th scope="col">Device</th><th scope="col">SVI</th><th scope="col">VRF</th><th scope="col">Subnet memberships</th></tr></thead>
        <tbody>{boundary.svis.map((svi) => <tr key={`${svi.deviceId}:${svi.interfaceName}`}><th scope="row"><DeviceLink devices={data.devices} deviceId={svi.deviceId} /></th><td>{svi.interfaceName}</td><td>{svi.vrf || '—'}</td><td>{deviceIds(svi.prefixes).join(', ') || '—'}</td></tr>)}</tbody>
      </table></div>
      {l3Source.asOf ? <small className="site-intelligence-boundary-caption">Selected L3 graph: {l3Source.asOf.toLocaleString(undefined, { timeZone: 'UTC', timeZoneName: 'short' })}</small> : null}
    </EvidenceGroup> : null}
    {fhrpSource && boundary.fhrpGroups.length ? <EvidenceGroup title="Configured first-hop groups">
      <div className="site-intelligence-boundary-groups">{boundary.fhrpGroups.map((group) => <article key={`${group.protocol}:${group.groupId}:${group.protectedPrefix}:${group.vrf ?? ''}`}>
        <h4>{group.protocol.toUpperCase()} group {group.groupId}</h4>
        <p>Protected subnet: {group.protectedPrefix}</p>
        {group.vrf ? <p>VRF: {group.vrf}</p> : null}
        <div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table">
          <caption className="sr-only">Configured participants in {group.protocol.toUpperCase()} group {group.groupId}{group.vrf ? ` in VRF ${group.vrf}` : ''}</caption>
          <thead><tr><th scope="col">Device</th><th scope="col">Interface</th><th scope="col">Virtual IPs</th><th scope="col">Configuration</th></tr></thead>
          <tbody>{group.participants.map((participant) => <tr key={`${participant.deviceId}:${participant.interfaceName}`}><th scope="row"><DeviceLink devices={data.devices} deviceId={participant.deviceId} /></th><td>{participant.interfaceName}</td><td>{deviceIds(participant.virtualIps).join(', ') || '—'}</td><td>{[participant.priority == null ? null : `Priority ${participant.priority}`, participant.preempt == null ? null : participant.preempt ? 'Preempt enabled' : 'Preempt disabled'].filter(Boolean).join(' · ')}</td></tr>)}</tbody>
        </table></div>
      </article>)}</div>
    </EvidenceGroup> : null}
    {data.boundaryFindings?.length ? <EvidenceGroup title="Boundary interpretation"><VlanFindingList findings={data.boundaryFindings} /></EvidenceGroup> : null}
  </>;
};

const VlanFindingEvidence = ({ data }: { data: SiteIntelligenceVlanFindingDetailResponse }) => {
  const vlans = data.finding.evidence.vlans;
  return <div className="site-intelligence-evidence">
    <section className="site-intelligence-interpretation-card">
      <span>Boundary interpretation</span>
      <div className="site-intelligence-interpretation-card__finding"><div><p>{data.finding.text}</p><VlanRationale text={data.finding.rationale} /></div><Confidence value={data.finding.confidence} /></div>
    </section>
    <EvidenceGroup title="Cited VLAN facts">
      <div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table">
        <caption className="sr-only">Selected graph facts cited by this boundary reading</caption>
        <thead><tr><th scope="col">VLAN</th><th scope="col">SVI observation</th><th scope="col">Configured first-hop groups</th></tr></thead>
        <tbody>{vlans.map((vlan) => <tr key={vlan.vlanId}><th scope="row">VLAN {vlan.vlanId}</th><td>{vlan.l3Boundary?.state === 'observed' ? `${new Set(vlan.l3Boundary.svis.map((svi) => svi.deviceId)).size} devices` : vlan.l3Boundary?.state === 'not_observed' ? 'No SVI resolved' : '—'}</td><td>{data.finding.evidence.boundarySources?.some((source) => source.purpose === 'fhrp_membership' && source.status === 'present') ? vlan.l3Boundary?.fhrpGroups.length ?? 0 : '—'}</td></tr>)}</tbody>
      </table></div>
      <p>Selected graph records show configured relationships; they do not establish the active gateway or current forwarding.</p>
    </EvidenceGroup>
    {vlans.map((vlan) => <details className="site-intelligence-boundary-evidence-detail" key={vlan.vlanId} open={vlans.length === 1}>
      <summary>VLAN {vlan.vlanId} source records</summary>
      <VlanBoundaryEvidence data={{ vlan, devices: data.devices, boundarySources: data.finding.evidence.boundarySources, boundaryFindings: [] }} />
      {vlan.l3Boundary?.state === 'not_observed' ? <p>No SVI was resolved for this VLAN in the selected graph.</p> : null}
    </details>)}
  </div>;
};

const VlanEvidence = ({ data }: { data: SiteIntelligenceVlanDetailResponse }) => {
  const membersByDevice = vlanMemberDeviceGroups(data);
  return <div className="site-intelligence-evidence">
    <section className="site-intelligence-interpretation-card">
      <span>Observed purpose</span>
      {data.purposes.length ? data.purposes.map((purpose) => <div key={purpose.findingId} className="site-intelligence-interpretation-card__finding"><div><p>{purpose.text}</p><VlanRationale text={purpose.rationale} /></div><Confidence value={purpose.confidence} /></div>) : <p>No purpose interpretation is available for this observed VLAN.</p>}
    </section>
    <dl className="site-intelligence-vlan-evidence-stats">
      <div><dt>Name status</dt><dd><span className={`site-intelligence-name-status site-intelligence-name-status--${data.vlan.nameStatus}`}>{data.vlan.nameStatus}</span></dd></div>
      <div><dt>Observed names</dt><dd>{data.vlan.nameObservations.length}</dd></div>
      <div><dt>Member devices</dt><dd>{data.vlan.memberDeviceCount}</dd></div>
      <div><dt>Member interfaces</dt><dd>{data.vlan.members.length}</dd></div>
    </dl>
    {data.commonalities.length ? <EvidenceGroup title="Common observed patterns"><VlanFindingList findings={data.commonalities} /></EvidenceGroup> : null}
    {data.observedDifferences.length ? <EvidenceGroup title="Observed naming differences"><VlanFindingList findings={data.observedDifferences} /></EvidenceGroup> : null}
    {data.reviewQuestions.length ? <EvidenceGroup title="Review questions"><VlanFindingList findings={data.reviewQuestions} /></EvidenceGroup> : null}
    <VlanBoundaryEvidence data={data} />
    <EvidenceGroup title="Observed names">
      {data.vlan.nameObservations.length ? <div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table">
        <caption className="sr-only">Observed VLAN names and the member devices that reported them</caption>
        <thead><tr><th scope="col">Observed name</th><th scope="col">Reporting devices</th><th scope="col">Device count</th></tr></thead>
        <tbody>{data.vlan.nameObservations.map((observation) => <tr key={observation.name}><th scope="row">{observation.name}</th><td><DeviceChipList devices={data.devices} deviceIds={observation.deviceIds} /></td><td>{deviceIds(observation.deviceIds).length}</td></tr>)}</tbody>
      </table></div> : <p>No member device reported a VLAN name.</p>}
    </EvidenceGroup>
    <EvidenceGroup title="Observed membership">
      {membersByDevice.length ? <div className="site-intelligence-table-wrap"><table className="site-intelligence-evidence-table site-intelligence-vlan-membership-table">
        <caption className="sr-only">Observed VLAN interface membership grouped by device</caption>
        <thead><tr><th scope="col">Device</th><th scope="col">Observed interfaces</th><th scope="col">Count</th></tr></thead>
        <tbody>{membersByDevice.map((member) => <tr key={member.deviceId}><th scope="row"><DeviceLink devices={data.devices} deviceId={member.deviceId} /></th><td><InterfaceChipList interfaceNames={member.interfaceNames} /></td><td>{member.interfaceNames.length}</td></tr>)}</tbody>
      </table></div> : <p>No interface memberships were observed for this VLAN.</p>}
    </EvidenceGroup>
  </div>;
};

export default SiteIntelligencePage;
